import { http, HttpResponse, delay, type HttpHandler } from 'msw';
import type { CameraId, IncidentId, SiteId, SspId, SsuId } from '@cguard/types';
import type {
  CameraResponse,
  ConfigurationResponse,
  IdentityMeResponse,
  LegalHoldResponse,
  PagedResultOfProvisioningReleaseResponse,
  ProvisioningArtifactResponse,
  ProvisioningReleaseResponse,
  SiraReportPreviewResponse,
  SiteResponse,
  SspRegistrationStatusResponse,
  UserResponse,
  WorkloadReleaseSummary,
} from '../generated/models';
import type {
  CameraSummary,
  IncidentDetail,
  IncidentSummary,
  SystemHealth,
  TimelineEntry,
} from '../view-models';
import type { ReviewSegment } from '../hooks/reviews';
import type { IncidentEvidence } from '../hooks/incident-evidence';
import { DEMO_CAMERA_ROSTER, lookupDemoCamera } from '../demo-camera-roster';
import synthFaceBase64 from './fixtures/synth-face.jpg.base64?raw';
import {
  CENTRAL_SSP_IDS,
  CENTRAL_SSU_IDS,
  DAY_MS,
  downtownSiteId,
  iso,
  marinaSiteId,
  mockDigest,
  mockUserId,
  now,
  serviceYardSiteId,
  siteNvrId,
} from './fixture-ids';
import { fleetOverviewCounters, fleetSiteHandlers, siteEdgeOnline } from './fleet-site-handlers';

import { canReadUser, type MockCaller } from './user-scope';

const synthFaceDataUrl = `data:image/jpeg;base64,${synthFaceBase64.replace(/\s+/g, '')}`;

const identityMe: IdentityMeResponse = {
  userId: '10000000-0000-4000-8000-000000000001',
  tier: 'sira',
  sspId: null,
  ssuId: null,
  siteId: null,
  roleNames: ['sira-admin', 'audit-viewer'],
  permissions: [
    { permissionKey: 'permissions.manage', requiredLoa: 1 },
    { permissionKey: 'audit.read', requiredLoa: 1 },
    { permissionKey: 'reports.read', requiredLoa: 1 },
    { permissionKey: 'legal-holds.manage', requiredLoa: 3 },
    { permissionKey: 'fleet.appliances.read', requiredLoa: 1 },
    { permissionKey: 'edge.commissioning.adjudicate', requiredLoa: 1 },
    { permissionKey: 'edge.fleet.admin', requiredLoa: 2 },
    { permissionKey: 'realtime.cross-tenant-subscribe', requiredLoa: 1 },
  ],
  scopeGrants: [],
  authzVersion: 1,
};

const registrationResumeToken = 'demo-registration-reference-keep-private';
let registrationStatus: SspRegistrationStatusResponse = createRegistrationStatus();

function createRegistrationStatus(): SspRegistrationStatusResponse {
  const timestamp = new Date().toISOString();
  return {
    registrationId: '01990f00-0000-7000-8000-000000000001',
    status: 'Draft',
    rejectionReason: null,
    emailVerified: false,
    mobileVerified: false,
    organizationName: null,
    createdAtUtc: timestamp,
    updatedAtUtc: timestamp,
  };
}

function hasDemoRegistrationToken(body: unknown): boolean {
  return (
    typeof body === 'object' &&
    body !== null &&
    'token' in body &&
    body.token === registrationResumeToken
  );
}

const incidents: IncidentSummary[] = [
  {
    id: 'inc-1001' as IncidentId,
    tier: 1,
    state: 'open',
    siteId: marinaSiteId as SiteId,
    siteName: 'Marina Tower',
    cameraId: 'cam-04' as CameraId,
    cameraLabel: 'Cam 04 · Garage Entry',
    source: 'ai',
    description: 'Person detected in armed zone after hours',
    receivedAt: iso(-30_000),
    confidence: 0.93,
    sspId: 'ssp-acme' as SspId,
    sspName: 'Acme Security',
    ssuId: 'ssu-marina' as SsuId,
    ssuName: 'Marina Holdings',
  },
  {
    id: 'inc-1002' as IncidentId,
    tier: 2,
    state: 'open',
    siteId: downtownSiteId as SiteId,
    siteName: 'Downtown Plaza',
    cameraId: 'cam-02' as CameraId,
    cameraLabel: 'Cam 02 · Loading Dock',
    source: 'panel',
    description: 'Zone 3 forced entry alert',
    receivedAt: iso(-4 * 60_000),
    sspId: 'ssp-acme' as SspId,
    sspName: 'Acme Security',
    ssuId: 'ssu-downtown' as SsuId,
    ssuName: 'Downtown JV',
  },
  {
    id: 'inc-1003' as IncidentId,
    tier: 4,
    state: 'open',
    siteId: serviceYardSiteId as SiteId,
    siteName: 'Service Yard',
    source: 'nvr',
    description: 'Motion at maintenance gate',
    receivedAt: iso(-12 * 60_000),
    confidence: 0.55,
    sspId: 'ssp-secure' as SspId,
    sspName: 'SecureCo',
    ssuId: 'ssu-yard' as SsuId,
    ssuName: 'Yard Logistics',
  },
];

const relatedAlarmIdsByIncidentId = new Map<string, string[]>();
const relatedDetectionIdsByIncidentId = new Map<string, string[]>();

const withCorrelationFields = (
  incident: IncidentSummary,
): IncidentSummary & {
  readonly incidentId: string;
  readonly status: string;
  readonly openedAtUtc: string;
  readonly relatedAlarmIds: readonly string[];
  readonly relatedDetectionIds: readonly string[];
} => ({
  ...incident,
  incidentId: incident.id,
  status: incident.state,
  openedAtUtc: incident.receivedAt,
  relatedAlarmIds: relatedAlarmIdsByIncidentId.get(incident.id) ?? [],
  relatedDetectionIds: relatedDetectionIdsByIncidentId.get(incident.id) ?? [],
});

type LegacyJsonAlarmRequest = Partial<{
  accountCode: string;
  eventCode: string;
  channelNumber: number | null;
  zoneNumber: number | null;
  occurredAtUtc: string;
  extraJson: string | null;
}>;

function parseLegacyAlarmDescription(extraJson: string | null | undefined): string | undefined {
  if (!extraJson) return undefined;
  try {
    const parsed = JSON.parse(extraJson) as { description?: unknown };
    return typeof parsed.description === 'string' && parsed.description.trim().length > 0
      ? parsed.description
      : undefined;
  } catch {
    return undefined;
  }
}

// 16-camera demo grid (#21). The shared roster owns ids, names and AI roles.
// This local table adds only transport status and site placement for API mocks.
const CAMERA_RUNTIME_BY_SHORT_ID: Readonly<
  Record<
    (typeof DEMO_CAMERA_ROSTER)[number]['shortId'],
    Pick<CameraSummary, 'siteId' | 'status' | 'latencyMs'>
  >
> = {
  'cam-01': { siteId: marinaSiteId as SiteId, status: 'live', latencyMs: 280 },
  'cam-02': { siteId: marinaSiteId as SiteId, status: 'live', latencyMs: 320 },
  'cam-03': { siteId: marinaSiteId as SiteId, status: 'live', latencyMs: 360 },
  'cam-04': { siteId: marinaSiteId as SiteId, status: 'live', latencyMs: 410 },
  'cam-05': { siteId: marinaSiteId as SiteId, status: 'connecting' },
  'cam-06': { siteId: marinaSiteId as SiteId, status: 'live', latencyMs: 380 },
  'cam-07': { siteId: downtownSiteId as SiteId, status: 'live', latencyMs: 310 },
  'cam-08': { siteId: downtownSiteId as SiteId, status: 'live', latencyMs: 290 },
  'cam-09': { siteId: downtownSiteId as SiteId, status: 'live', latencyMs: 340 },
  'cam-10': { siteId: downtownSiteId as SiteId, status: 'live', latencyMs: 420 },
  'cam-11': { siteId: downtownSiteId as SiteId, status: 'live', latencyMs: 370 },
  'cam-12': { siteId: downtownSiteId as SiteId, status: 'offline' },
  'cam-13': { siteId: downtownSiteId as SiteId, status: 'connecting' },
  'cam-14': { siteId: serviceYardSiteId as SiteId, status: 'live', latencyMs: 460 },
  'cam-15': { siteId: serviceYardSiteId as SiteId, status: 'live', latencyMs: 520 },
  'cam-16': { siteId: serviceYardSiteId as SiteId, status: 'live', latencyMs: 490 },
};

type VideoConsoleMockCamera = CameraSummary & {
  nvrId: string | null;
  playbackConfigured: boolean;
  tier: string;
  vendor: string;
  model: string;
};

const cameras: VideoConsoleMockCamera[] = DEMO_CAMERA_ROSTER.map((camera, index) => ({
  id: camera.shortId as CameraId,
  nvrId: siteNvrId(CAMERA_RUNTIME_BY_SHORT_ID[camera.shortId].siteId),
  name: camera.name,
  ...CAMERA_RUNTIME_BY_SHORT_ID[camera.shortId],
  playbackConfigured: index !== 4 && index !== 11,
  tier: index % 5 === 0 ? 'TierB' : index % 7 === 0 ? 'TierMotion' : 'TierC',
  vendor: index % 3 === 0 ? 'Hikvision' : 'Onvif',
  model: index % 3 === 0 ? 'DS-2CD2386G2-IU' : 'P3265-LVE',
}));

const nvrProbeCandidates = [
  {
    key: 'hikvision:1',
    name: 'Ambulance Bay East',
    channelNumber: 1,
    resolution: '1920x1080',
    codec: 'H264',
    recording: true,
    playbackAvailable: true,
  },
  {
    key: 'hikvision:2',
    name: 'Main Entry Canopy',
    channelNumber: 2,
    resolution: '2560x1440',
    codec: 'H265',
    recording: true,
    playbackAvailable: true,
  },
  {
    key: 'hikvision:3',
    name: 'Emergency Exit North',
    channelNumber: 3,
    resolution: '1920x1080',
    codec: 'H264',
    recording: true,
    playbackAvailable: true,
  },
  {
    key: 'hikvision:4',
    name: 'Visitor Drop-off',
    channelNumber: 4,
    resolution: '1920x1080',
    codec: 'H264',
    recording: true,
    playbackAvailable: true,
  },
];

function showcaseSourceCameraId(id: string): CameraId | null {
  const camera = lookupDemoCamera(id);
  return camera ? (camera.shortId as CameraId) : null;
}

function mockCameraById(id: unknown): VideoConsoleMockCamera | undefined {
  if (typeof id !== 'string') return undefined;
  const found = cameras.find((camera) => camera.id === id);
  if (found) return found;

  const alias = lookupDemoCamera(id);
  if (!alias || alias.shortId === id) return undefined;
  const source = cameras.find((camera) => camera.id === alias.shortId);
  if (!source) return undefined;

  return {
    ...source,
    id: alias.stableId as CameraId,
    name: alias.name,
  };
}

/** Keep view models inside the demo; expose Central's DTO at the HTTP boundary. */
function cameraResponse(camera: VideoConsoleMockCamera): CameraResponse {
  return {
    cameraId: camera.id,
    siteId: camera.siteId,
    nvrId: camera.nvrId,
    channelNumber:
      cameras.findIndex((entry) => entry.id === (lookupDemoCamera(camera.id)?.shortId ?? camera.id)) + 1,
    vendor: camera.vendor,
    model: camera.model,
    macAddress: null,
    ipAddress: '192.0.2.10',
    resolutionMain: '1920x1080',
    resolutionSub: null,
    frameRateMain: 25,
    frameRateSub: null,
    codecMain: 'H264',
    codecSub: null,
    aiArmedZones: [],
    tier: camera.tier,
    status: camera.status === 'live' ? 'Active' : camera.status === 'offline' ? 'Disabled' : 'PendingBind',
    createdAtUtc: iso(-30 * DAY_MS),
    lastSeenUtc: camera.status === 'live' ? iso(0) : null,
    playbackConfigured: camera.playbackConfigured,
  };
}

/** Site fixtures in the central `SiteResponse` wire shape, owned by the central SSP/SSU ids. */
function siteFixture(
  site: Pick<
    SiteResponse,
    'siteId' | 'owningSsuId' | 'owningSspId' | 'name' | 'city' | 'emirate' | 'status'
  > &
    Partial<SiteResponse>,
): SiteResponse {
  const live = site.status === 'Live';
  return {
    addressLine1: `${site.name}, ${site.city}`,
    addressLine2: null,
    latitude: 25.08,
    longitude: 55.14,
    tier: 'Commercial',
    billingTier: 'Tier2',
    scenarioProfileKey: 'commercial-default',
    goLiveBasis: live ? 'ComplianceCertificate' : null,
    currentComplianceCertificateId: live ? `cert-${site.siteId}` : null,
    currentComplianceExceptionId: null,
    currentComplianceFollowUpCaseId: null,
    networkIntegrationMode: 'CGuardOwned',
    networkIntegrationDesignationId: null,
    networkIntegrationDesignationVersion: null,
    networkIntegrationDesignatedByUserId: null,
    networkIntegrationDesignatedAtUtc: null,
    createdAtUtc: '2026-06-01T08:00:00Z',
    wentLiveAtUtc: live ? '2026-07-01T08:00:00Z' : null,
    decommissionedAtUtc: null,
    edgeOnline: siteEdgeOnline(site.siteId),
    incidents24h: incidents.filter((incident) => incident.siteId === site.siteId && Date.parse(incident.receivedAt) >= Date.now() - DAY_MS).length,
    audioEnabledClasses: ['scream', 'alarm_bell'],
    ...site,
  };
}

const sites: SiteResponse[] = [
  siteFixture({
    siteId: marinaSiteId,
    owningSsuId: CENTRAL_SSU_IDS['ssu-marina'],
    owningSspId: CENTRAL_SSP_IDS['ssp-acme'],
    name: 'Marina Tower',
    city: 'Dubai',
    emirate: 'Dubai',
    status: 'Live',
  }),
  siteFixture({
    siteId: downtownSiteId,
    owningSsuId: CENTRAL_SSU_IDS['ssu-downtown'],
    owningSspId: CENTRAL_SSP_IDS['ssp-acme'],
    name: 'Downtown Plaza',
    city: 'Dubai',
    emirate: 'Dubai',
    status: 'Live',
    tier: 'HighRisk',
  }),
  siteFixture({
    siteId: serviceYardSiteId,
    owningSsuId: CENTRAL_SSU_IDS['ssu-yard'],
    owningSspId: CENTRAL_SSP_IDS['ssp-secure'],
    name: 'Service Yard',
    city: 'Sharjah',
    emirate: 'Sharjah',
    status: 'Provisioning',
    latitude: 25.35,
    longitude: 55.42,
  }),
];

type MockIncidentDetail = Omit<IncidentDetail, 'cameras'> & {
  readonly coverageState: string;
  readonly coverageNoCoverageReason: string | null;
  readonly zoneCameraMappingVersionId: string | null;
  readonly zoneCameraMappingVersionNumber: number | null;
  readonly cameras: Array<
    CameraSummary & {
      readonly coverageType: string;
      readonly aiPriority: number;
    }
  >;
};

const ALARM_RAISED_BY: Record<string, string> = {
  ai: 'AI detection raised the alarm',
  panel: 'The intrusion panel raised the alarm',
  nvr: 'The recorder raised the alarm',
};

// Acknowledge, dispatch and close add to this, so the detail keeps the operator's steps.
const incidentActionTimeline = new Map<string, TimelineEntry[]>();

const detailFor = (s: IncidentSummary): MockIncidentDetail => ({
  ...s,
  timeline: [
    {
      id: 't1',
      kind: 'alarm',
      at: s.receivedAt,
      description: ALARM_RAISED_BY[s.source] ?? 'Alarm received',
      actor: 'Edge agent',
    },
    {
      id: 't2',
      kind: 'ai-verdict',
      at: iso(-25_000),
      description: 'Possible face match, 86% confidence',
      actor: 'AI orchestrator',
    },
    ...(incidentActionTimeline.get(s.id) ?? []),
  ],
  coverageState: s.id === 'inc-1003' ? 'NoCoverage' : 'Versioned',
  coverageNoCoverageReason: s.id === 'inc-1003' ? 'No commissioned panel-zone mapping' : null,
  zoneCameraMappingVersionId: s.id === 'inc-1003' ? null : `mapping-${s.siteId}`,
  zoneCameraMappingVersionNumber: s.id === 'inc-1003' ? null : 3,
  cameras: cameras
    .filter((camera) => camera.siteId === s.siteId)
    .map((camera, index) => ({
      ...camera,
      coverageType:
        camera.id === s.cameraId || (!s.cameraId && index === 0) ? 'Primary' : 'Context',
      aiPriority: index,
    })),
});

const evidenceFrameUri = '/v1/mock-evidence/frames/lobby-1.svg';
const evidenceFrame2Uri = '/v1/mock-evidence/frames/lobby-2.svg';
const evidenceFaceCropUri = '/v1/mock-evidence/crops/face-1.jpg';
const sharedFaceSourceFrameId = '21a4c4f8f7c2d4b6f4b5da94c6e75a1717cfa84e399bc905fdd95f14b902ece3';
const sharedFaceSourceFrameAtUtc = iso(-28_000);
const detectorEvidenceProcessing = {
  state: 'succeeded' as const,
  failureReason: null,
  startedAtUtc: iso(-28_100),
  updatedAtUtc: iso(-27_600),
  completedAtUtc: iso(-27_600),
  provenance: {
    producer: 'detector-evidence-uploader',
    model: 'scrfd-2.5g',
    version: 'opset13',
    device: 'CPU',
    stage: null,
    promptVersion: null,
  },
};
const populatedEvidence: IncidentEvidence = {
  incidentId: 'inc-1001' as IncidentId,
  generatedAtUtc: iso(0),
  correlationPolicy: 'ExactAlarmId',
  alarmRecordingStatus: 'available',
  alarmRecordingCoverageOutcomes: [],
  clips: [
    {
      clipId: '11111111-1111-4111-8111-111111111101',
      edgeClipRequestId: 'edge-clip-inc-1001-primary',
      sourceAlarmEventId: 'alarm-demo-inc-1001',
      schemaVersion: 2,
      cameraId: 'cam-04',
      recordedFromUtc: iso(-60_000),
      recordedToUtc: iso(30_000),
      alarmTriggeredAtUtc: iso(-30_000),
      alarmOffsetSeconds: 30,
      requestedFromUtc: iso(-60_000),
      requestedToUtc: iso(30_000),
      durationSeconds: 90,
      resolutionPx: '1920x1080',
      codec: 'h264',
      byteSize: 4_218_344,
      status: 'available',
      captureState: 'available',
      failureReason: null,
      edgeSha256: '01a4c4f8f7c2d4b6f4b5da94c6e75a1717cfa84e399bc905fdd95f14b902ece1',
      centralSha256: '01a4c4f8f7c2d4b6f4b5da94c6e75a1717cfa84e399bc905fdd95f14b902ece1',
      chainOfCustodyVerified: true,
      retentionExpiresAtUtc: iso(90 * 24 * 60 * 60_000),
    },
    {
      clipId: '11111111-1111-4111-8111-111111111102',
      edgeClipRequestId: 'edge-clip-inc-1001-context-1',
      sourceAlarmEventId: 'alarm-demo-inc-1001',
      schemaVersion: 2,
      cameraId: 'cam-01',
      recordedFromUtc: iso(-60_000),
      recordedToUtc: iso(30_000),
      alarmTriggeredAtUtc: iso(-30_000),
      alarmOffsetSeconds: 30,
      requestedFromUtc: iso(-60_000),
      requestedToUtc: iso(30_000),
      durationSeconds: 90,
      resolutionPx: '1280x720',
      codec: 'h264',
      byteSize: 2_618_344,
      status: 'available',
      captureState: 'available',
      failureReason: null,
      edgeSha256: '12a4c4f8f7c2d4b6f4b5da94c6e75a1717cfa84e399bc905fdd95f14b902ece2',
      centralSha256: '12a4c4f8f7c2d4b6f4b5da94c6e75a1717cfa84e399bc905fdd95f14b902ece2',
      chainOfCustodyVerified: true,
      retentionExpiresAtUtc: iso(90 * 24 * 60 * 60_000),
    },
  ],
  alarmRecordings: [
    {
      evidenceId: '11111111-1111-4111-8111-111111111201',
      manifestEventId: 'spool-complete-inc-1001',
      edgeClipRequestId: 'edge-clip-inc-1001-primary',
      sourceAlarmEventId: 'alarm-demo-inc-1001',
      centralClipId: '11111111-1111-4111-8111-111111111101',
      cameraId: 'cam-04',
      schemaVersion: 2,
      alarmTriggeredAtUtc: iso(-30_000),
      requestedFromUtc: iso(-60_000),
      requestedToUtc: iso(30_000),
      observedFromUtc: iso(-60_000),
      observedToUtc: iso(30_000),
      capturedAtUtc: iso(30_000),
      state: 'available',
      failureReason: null,
      playableSha256: '01a4c4f8f7c2d4b6f4b5da94c6e75a1717cfa84e399bc905fdd95f14b902ece1',
      playableSizeBytes: 4_218_344,
      source: 'local-spool',
      manifestBuiltAtUtc: iso(31_000),
      nvrOfflineAtAlarm: true,
      nvrId: siteNvrId(marinaSiteId),
      nvrLastHealthProbeAtUtc: iso(-29_900),
      nvrLastProbeStatus: 'TIMEOUT',
      nvrLastSuccessfulFetchAtUtc: iso(-330_000),
      nvrVendorReported: 'probe deadline exceeded',
    },
    {
      evidenceId: '11111111-1111-4111-8111-111111111202',
      manifestEventId: 'spool-complete-inc-1001',
      edgeClipRequestId: 'edge-clip-inc-1001-context-1',
      sourceAlarmEventId: 'alarm-demo-inc-1001',
      centralClipId: '11111111-1111-4111-8111-111111111102',
      cameraId: 'cam-01',
      schemaVersion: 2,
      alarmTriggeredAtUtc: iso(-30_000),
      requestedFromUtc: iso(-60_000),
      requestedToUtc: iso(30_000),
      observedFromUtc: iso(-60_000),
      observedToUtc: iso(30_000),
      capturedAtUtc: iso(30_000),
      state: 'available',
      failureReason: null,
      playableSha256: '12a4c4f8f7c2d4b6f4b5da94c6e75a1717cfa84e399bc905fdd95f14b902ece2',
      playableSizeBytes: 2_618_344,
      source: 'local-spool',
      manifestBuiltAtUtc: iso(31_000),
      nvrOfflineAtAlarm: true,
      nvrId: siteNvrId(marinaSiteId),
      nvrLastHealthProbeAtUtc: iso(-29_900),
      nvrLastProbeStatus: 'TIMEOUT',
      nvrLastSuccessfulFetchAtUtc: iso(-330_000),
      nvrVendorReported: 'probe deadline exceeded',
    },
  ],
  detections: [
    {
      detectionId: '22222222-2222-4222-8222-222222222201',
      alarmId: 'alarm-demo-inc-1001',
      sourceEventId: 'edge-face-001',
      sourceFrameId: sharedFaceSourceFrameId,
      cameraId: 'cam-04',
      sourceFrameAtUtc: sharedFaceSourceFrameAtUtc,
      emittedAtUtc: iso(-27_700),
      eventClass: 'Face',
      modelFamily: 'scrfd-2.5g',
      modelVersion: 'opset13',
      inferenceDevice: 'CPU',
      confidence: 0.86,
      boundingBox: { x: 690, y: 210, width: 330, height: 440 },
      frameWidth: 1920,
      frameHeight: 1080,
      trackId: 41,
      trackScope: 'camera-session',
      fullFrame: {
        status: 'available',
        uri: evidenceFrameUri,
        sha256: sharedFaceSourceFrameId,
        byteLength: 98_210,
        capturedAtUtc: sharedFaceSourceFrameAtUtc,
        captureTimestampVerification: 'edge-sealed',
        contentBase64: null,
      },
      detectorCrop: {
        status: 'available',
        uri: evidenceFaceCropUri,
        sha256: '31a4c4f8f7c2d4b6f4b5da94c6e75a1717cfa84e399bc905fdd95f14b902ece4',
        byteLength: 18_210,
        capturedAtUtc: iso(-28_000),
        captureTimestampVerification: 'derived-from-sealed-frame',
        contentBase64: null,
      },
      detectorProvenance: {
        model: 'scrfd-2.5g',
        version: 'opset13',
        device: 'CPU',
      },
      embedderProvenance: {
        model: 'EDGEFACE-EDGE',
        version: '2026.07',
        device: 'iGPU',
      },
      identityMatchingStatus: 'matched',
      watchlistMatches: [
        {
          matchId: '44444444-4444-4444-8444-444444444401',
          watchlistId: '44444444-4444-4444-8444-444444444402',
          entryId: '44444444-4444-4444-8444-444444444403',
          label: 'ChokePoint evaluation subject 01',
          matchType: 'Face',
          similarity: 0.92,
          similarityThreshold: 0.6,
          decision: 'Matched',
          matcherModel: 'EDGEFACE-EDGE',
          matcherVersion: '2026.07',
          matcherDevice: 'iGPU',
          matchedAtUtc: iso(-27_700),
          enrolledReference: {
            status: 'available',
            uri: '/v1/incidents/inc-1001/evidence/watchlist-matches/match-001/reference',
            sha256: 'c9427188d17d552b5b71dfd9d803f0106f7cc7ab0d8aa41a1d68584d83fdd853',
            byteLength: 18_620,
            capturedAtUtc: null,
            captureTimestampVerification: null,
            contentBase64: null,
          },
        },
      ],
      processing: detectorEvidenceProcessing,
    },
    {
      detectionId: '22222222-2222-4222-8222-222222222202',
      alarmId: 'alarm-demo-inc-1001',
      sourceEventId: 'edge-face-002',
      sourceFrameId: sharedFaceSourceFrameId,
      cameraId: 'cam-04',
      sourceFrameAtUtc: sharedFaceSourceFrameAtUtc,
      emittedAtUtc: iso(-27_500),
      eventClass: 'Face',
      modelFamily: 'scrfd-2.5g',
      modelVersion: 'opset13',
      inferenceDevice: 'CPU',
      confidence: 0.79,
      boundingBox: { x: 310, y: 260, width: 250, height: 350 },
      frameWidth: 1920,
      frameHeight: 1080,
      trackId: 42,
      trackScope: 'camera-session',
      fullFrame: {
        status: 'available',
        uri: evidenceFrameUri,
        sha256: sharedFaceSourceFrameId,
        byteLength: 98_210,
        capturedAtUtc: sharedFaceSourceFrameAtUtc,
        captureTimestampVerification: 'edge-sealed',
        contentBase64: null,
      },
      detectorCrop: {
        status: 'available',
        uri: evidenceFaceCropUri,
        sha256: '51a4c4f8f7c2d4b6f4b5da94c6e75a1717cfa84e399bc905fdd95f14b902ece6',
        byteLength: 16_420,
        capturedAtUtc: iso(-27_800),
        captureTimestampVerification: 'derived-from-sealed-frame',
        contentBase64: null,
      },
      detectorProvenance: {
        model: 'scrfd-2.5g',
        version: 'opset13',
        device: 'CPU',
      },
      embedderProvenance: {
        model: 'EDGEFACE-EDGE',
        version: '2026.07',
        device: 'iGPU',
      },
      identityMatchingStatus: 'no-match',
      watchlistMatches: [],
      processing: detectorEvidenceProcessing,
    },
    {
      detectionId: '22222222-2222-4222-8222-222222222203',
      alarmId: 'alarm-demo-inc-1001',
      sourceEventId: 'edge-face-003',
      sourceFrameId: sharedFaceSourceFrameId,
      cameraId: 'cam-04',
      sourceFrameAtUtc: sharedFaceSourceFrameAtUtc,
      emittedAtUtc: iso(-26_900),
      eventClass: 'Face',
      modelFamily: 'scrfd-2.5g',
      modelVersion: 'opset13',
      inferenceDevice: 'CPU',
      confidence: 0.74,
      boundingBox: { x: 1180, y: 245, width: 240, height: 330 },
      frameWidth: 1920,
      frameHeight: 1080,
      trackId: 43,
      trackScope: 'camera-session',
      fullFrame: {
        status: 'available',
        uri: evidenceFrameUri,
        sha256: sharedFaceSourceFrameId,
        byteLength: 98_210,
        capturedAtUtc: sharedFaceSourceFrameAtUtc,
        captureTimestampVerification: 'edge-sealed',
        contentBase64: null,
      },
      detectorCrop: {
        status: 'available',
        uri: evidenceFaceCropUri,
        sha256: '62a4c4f8f7c2d4b6f4b5da94c6e75a1717cfa84e399bc905fdd95f14b902ece8',
        byteLength: 15_870,
        capturedAtUtc: iso(-27_200),
        captureTimestampVerification: 'derived-from-sealed-frame',
        contentBase64: null,
      },
      detectorProvenance: {
        model: 'scrfd-2.5g',
        version: 'opset13',
        device: 'CPU',
      },
      embedderProvenance: {
        model: 'EDGEFACE-EDGE',
        version: '2026.07',
        device: 'iGPU',
      },
      identityMatchingStatus: 'not-performed',
      watchlistMatches: [],
      processing: detectorEvidenceProcessing,
    },
  ],
  sceneDescriptions: [
    {
      sceneEventId: '33333333-3333-4333-8333-333333333301',
      alarmEventId: 'alarm-demo-inc-1001',
      cameraId: 'cam-04',
      sourceFrameAtUtc: iso(-27_000),
      caption: 'Two people moving through the lobby entrance',
      description: 'Two people enter through the lobby and continue toward the inner doorway.',
      vlmModelId: 'qwen25-vl-3b-int4',
      vlmModelVersion: '2025.01',
      promptVersion: 'alarm-scene-v2',
      stage: 'stage2',
      status: 'available',
      sourceFrames: [
        {
          status: 'available',
          sha256: '71a4c4f8f7c2d4b6f4b5da94c6e75a1717cfa84e399bc905fdd95f14b902ece8',
          byteLength: 112_045,
          capturedAtUtc: iso(-27_000),
          captureTimestampVerification: 'edge-sealed',
          alarmEventId: 'alarm-demo-inc-1001',
          sourceUri: evidenceFrameUri,
          contentBase64: null,
          regions: [
            {
              kind: 'person',
              boundingBox: { x: 310, y: 260, width: 250, height: 350 },
              sha256: null,
              contentBase64: null,
            },
            {
              kind: 'person',
              boundingBox: { x: 690, y: 210, width: 330, height: 440 },
              sha256: null,
              contentBase64: null,
            },
          ],
        },
        {
          status: 'available',
          sha256: '81a4c4f8f7c2d4b6f4b5da94c6e75a1717cfa84e399bc905fdd95f14b902ece9',
          byteLength: 118_312,
          capturedAtUtc: iso(-26_500),
          captureTimestampVerification: 'edge-sealed',
          alarmEventId: 'alarm-demo-inc-1001',
          sourceUri: evidenceFrame2Uri,
          contentBase64: null,
          regions: [],
        },
      ],
      legacyFrameRefs: [],
    },
  ],
  vlmProcesses: [
    {
      alarmEventId: 'alarm-demo-inc-1001',
      cameraId: 'cam-04',
      processing: {
        state: 'succeeded',
        failureReason: null,
        startedAtUtc: iso(-30_000),
        updatedAtUtc: iso(-26_000),
        completedAtUtc: iso(-26_000),
        provenance: {
          producer: 'alarm-vlm',
          model: 'qwen25-vl-3b-int4',
          version: '2025.01',
          device: null,
          stage: 'stage2',
          promptVersion: 'alarm-scene-v2',
        },
      },
    },
  ],
};

const degradedEvidence: IncidentEvidence = {
  incidentId: 'inc-1002' as IncidentId,
  generatedAtUtc: iso(0),
  correlationPolicy: 'ExactAlarmId',
  alarmRecordingStatus: 'partial',
  alarmRecordingCoverageOutcomes: [],
  clips: [
    {
      ...populatedEvidence.clips[0]!,
      clipId: '11111111-1111-4111-8111-111111111103',
      edgeClipRequestId: 'edge-clip-inc-1002-primary',
      sourceAlarmEventId: 'alarm-demo-inc-1002',
      cameraId: 'cam-02',
      status: 'partial',
      chainOfCustodyVerified: false,
      centralSha256: null,
    },
  ],
  alarmRecordings: [
    {
      ...populatedEvidence.alarmRecordings[0]!,
      evidenceId: '11111111-1111-4111-8111-111111111203',
      manifestEventId: 'spool-complete-inc-1002',
      edgeClipRequestId: 'edge-clip-inc-1002-primary',
      sourceAlarmEventId: 'alarm-demo-inc-1002',
      centralClipId: '11111111-1111-4111-8111-111111111103',
      cameraId: 'cam-02',
      observedFromUtc: iso(-44_000),
      state: 'partial',
      failureReason: 'Only 14 seconds of the requested 30-second pre-roll remained in the ring.',
    },
    {
      ...populatedEvidence.alarmRecordings[1]!,
      evidenceId: '11111111-1111-4111-8111-111111111204',
      manifestEventId: 'spool-complete-inc-1002',
      edgeClipRequestId: 'edge-clip-inc-1002-context-1',
      sourceAlarmEventId: 'alarm-demo-inc-1002',
      centralClipId: null,
      cameraId: 'cam-03',
      observedFromUtc: null,
      observedToUtc: null,
      capturedAtUtc: null,
      state: 'pre-roll-evicted',
      failureReason: 'The requested alarm window was evicted before sealing completed.',
      playableSha256: null,
      playableSizeBytes: null,
    },
  ],
  detections: [
    {
      ...populatedEvidence.detections[0]!,
      detectionId: '22222222-2222-4222-8222-222222222204',
      alarmId: 'alarm-demo-inc-1002',
      sourceFrameId: null,
      cameraId: 'cam-02',
      fullFrame: {
        status: 'unavailable',
        uri: null,
        sha256: null,
        byteLength: null,
        capturedAtUtc: null,
        captureTimestampVerification: null,
        contentBase64: null,
      },
      detectorCrop: {
        status: 'unavailable',
        uri: null,
        sha256: null,
        byteLength: null,
        capturedAtUtc: null,
        captureTimestampVerification: null,
        contentBase64: null,
      },
      processing: {
        state: 'failed',
        failureReason: 'evidence-store-write-failed',
        startedAtUtc: iso(-28_100),
        updatedAtUtc: iso(-27_000),
        completedAtUtc: iso(-27_000),
        provenance: detectorEvidenceProcessing.provenance,
      },
    },
  ],
  sceneDescriptions: [
    {
      ...populatedEvidence.sceneDescriptions[0]!,
      sceneEventId: '33333333-3333-4333-8333-333333333302',
      alarmEventId: 'alarm-demo-inc-1002',
      cameraId: 'cam-02',
      status: 'pending',
      sourceFrames: [],
    },
  ],
  vlmProcesses: [
    {
      alarmEventId: 'alarm-demo-inc-1002',
      cameraId: 'cam-02',
      processing: {
        state: 'pending',
        failureReason: null,
        startedAtUtc: iso(-30_000),
        updatedAtUtc: iso(-27_000),
        completedAtUtc: null,
        provenance: {
          producer: 'alarm-vlm',
          model: 'qwen25-vl-3b-int4',
          version: '2025.01',
          device: null,
          stage: 'stage1',
          promptVersion: 'alarm-scene-v2',
        },
      },
    },
  ],
};

const mockIncidentClipMp4Base64 =
  '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';

function mockLobbyEvidenceFrame(offset: number): string {
  const foregroundX = 690 + offset;
  const backgroundX = 310 - Math.round(offset / 2);
  return `<svg xmlns="http://www.w3.org/2000/svg" width="1920" height="1080" viewBox="0 0 1920 1080">
  <defs><linearGradient id="wall" x1="0" y1="0" x2="0" y2="1"><stop stop-color="#5d6268"/><stop offset="1" stop-color="#242a30"/></linearGradient></defs>
  <rect width="1920" height="1080" fill="url(#wall)"/>
  <path d="M0 0h430l250 1080H0z" fill="#7e858c"/><path d="M1920 0h-430l-250 1080h680z" fill="#6e747a"/>
  <path d="M500 1080 760 190h400l260 890z" fill="#15191d"/><rect x="760" y="85" width="400" height="150" fill="#282e34"/>
  <image href="${synthFaceDataUrl}" x="${backgroundX}" y="260" width="250" height="350" preserveAspectRatio="xMidYMid slice"/>
  <image href="${synthFaceDataUrl}" x="${foregroundX}" y="210" width="330" height="440" preserveAspectRatio="xMidYMid slice"/>
  <path d="M${backgroundX - 45} 610h340l90 470H${backgroundX - 110}z" fill="#39434d"/>
  <path d="M${foregroundX - 80} 650h490l130 430H${foregroundX - 190}z" fill="#233d4d"/>
  <text x="42" y="1030" fill="#d8e1e8" font-family="monospace" font-size="30">Sample data · Lobby entrance</text>
</svg>`;
}

const health: SystemHealth = {
  overall: 'degraded',
  components: [
    {
      name: 'npgsql',
      status: 'healthy',
      tags: ['db', 'ready'],
      description: null,
      durationMs: 4.2,
    },
    {
      name: 'redis',
      status: 'healthy',
      tags: ['cache', 'ready'],
      description: null,
      durationMs: 1.8,
    },
    {
      name: 'seaweedfs',
      status: 'healthy',
      tags: ['storage', 'ready'],
      description: null,
      durationMs: 12.6,
    },
    {
      name: 'keycloak',
      status: 'degraded',
      tags: ['auth'],
      description: 'token endpoint p95 above 500ms',
      durationMs: 38.1,
    },
    {
      name: 'wolverine-bus',
      status: 'healthy',
      tags: ['messaging', 'ready'],
      description: null,
      durationMs: 2.9,
    },
  ],
  lastCheckedUtc: iso(0),
  durationMs: 41.7,
};

interface SemanticSearchBody {
  query?: string;
  limit?: number;
  minScore?: number;
  siteId?: string;
  cameraId?: string;
  since?: string;
  before?: string;
}

interface MockReviewSearchResult {
  segmentId: string;
  tenantId: string;
  siteId: string;
  cameraId: string;
  score: number;
  segmentStartTime: string;
  representativeThumbUri: string | null;
  text: string;
}

interface MockSceneSearchResult {
  alarmEventId: string;
  eventKind: string;
  sceneEventId: string;
  tenantId: string;
  siteId: string;
  cameraId: string;
  score: number;
  sceneTimestamp: string;
  sceneCaption: string;
  sceneDescription: string;
  vlmModelId: string;
  vlmModelVersion: string;
  stage: string | null;
}

const demoTenantId = '11111111-1111-1111-1111-111111111111';

const semanticReviewResults: MockReviewSearchResult[] = [
  {
    segmentId: '77777777-7777-7777-7777-777777777001',
    tenantId: demoTenantId,
    siteId: marinaSiteId,
    cameraId: '66666666-6666-6666-6666-666666666601',
    score: 0.87,
    segmentStartTime: iso(-9 * 60_000),
    representativeThumbUri: 's3://cguard-demo/reviews/face-lobby-thumb.jpg',
    text: 'lobby face person entrance staff queue',
  },
  {
    segmentId: '77777777-7777-7777-7777-777777777002',
    tenantId: demoTenantId,
    siteId: marinaSiteId,
    cameraId: '66666666-6666-6666-6666-666666666602',
    score: 0.79,
    segmentStartTime: iso(-17 * 60_000),
    representativeThumbUri: 's3://cguard-demo/reviews/object-counter-thumb.jpg',
    text: 'person object reception counter bag chair waiting area',
  },
  {
    segmentId: '77777777-7777-7777-7777-777777777003',
    tenantId: demoTenantId,
    siteId: downtownSiteId,
    cameraId: '66666666-6666-6666-6666-666666666608',
    score: 0.72,
    segmentStartTime: iso(-24 * 60_000),
    representativeThumbUri: null,
    text: 'object detection corridor people sofa lounge motion',
  },
];

// Camera ids match the MSW camera list (cam-NN), so the review screens name each camera.
const reviewSegments = [
  {
    segmentId: '77777777-7777-7777-7777-777777777001',
    tenantId: demoTenantId,
    sspId: '22222222-2222-2222-2222-222222222201',
    siteId: marinaSiteId,
    cameraId: 'cam-01',
    startTime: iso(-9 * 60_000),
    endTime: iso(-8 * 60_000),
    closedAt: iso(-8 * 60_000),
    severity: 'alert',
    representativeThumbUri: 's3://cguard-demo/reviews/face-lobby-thumb.jpg',
    representativeThumbContentType: 'image/jpeg',
    data: {
      labels: ['face-candidate', 'person'],
      zones: ['lobby-entrance'],
      eventIds: ['event-demo-face-001', 'event-demo-person-001'],
      confidenceMax: 0.93,
      confidenceMin: 0.78,
    },
    latestStatus: 'closed',
  },
  {
    segmentId: '77777777-7777-7777-7777-777777777002',
    tenantId: demoTenantId,
    sspId: '22222222-2222-2222-2222-222222222201',
    siteId: marinaSiteId,
    cameraId: 'cam-02',
    startTime: iso(-17 * 60_000),
    endTime: iso(-15 * 60_000),
    closedAt: iso(-15 * 60_000),
    severity: 'detection',
    representativeThumbUri: 's3://cguard-demo/reviews/object-counter-thumb.jpg',
    representativeThumbContentType: 'image/jpeg',
    data: {
      labels: ['person', 'bag'],
      zones: ['reception-counter'],
      eventIds: ['event-demo-object-001'],
      confidenceMax: 0.86,
      confidenceMin: 0.71,
    },
    latestStatus: 'closed',
  },
  {
    segmentId: '77777777-7777-7777-7777-777777777003',
    tenantId: demoTenantId,
    sspId: '22222222-2222-2222-2222-222222222202',
    siteId: downtownSiteId,
    cameraId: 'cam-08',
    startTime: iso(-24 * 60_000),
    endTime: iso(-22 * 60_000),
    closedAt: iso(-22 * 60_000),
    severity: 'detection',
    representativeThumbUri: null,
    representativeThumbContentType: null,
    data: {
      labels: ['person'],
      zones: ['lounge-corridor'],
      eventIds: ['event-demo-object-002', 'event-demo-object-003'],
      confidenceMax: 0.81,
      confidenceMin: 0.67,
    },
    latestStatus: 'closed',
  },
] as const satisfies ReadonlyArray<ReviewSegment>;

const semanticSceneResults: MockSceneSearchResult[] = [
  {
    alarmEventId: 'alarm-demo-001',
    eventKind: 'scene_described',
    sceneEventId: '88888888-8888-8888-8888-888888888001',
    tenantId: demoTenantId,
    siteId: marinaSiteId,
    cameraId: '66666666-6666-6666-6666-666666666606',
    score: 0.91,
    sceneTimestamp: iso(-5 * 60_000),
    sceneCaption: 'Person moving through lobby reception area',
    sceneDescription:
      'A person crosses the reception floor while another person remains seated near the lounge.',
    vlmModelId: 'qwen2.5-vl-3b',
    vlmModelVersion: 'qwen2.5-vl-3b@edge-demo',
    stage: 'alarm-burst',
  },
  {
    alarmEventId: 'alarm-demo-002',
    eventKind: 'scene_described',
    sceneEventId: '88888888-8888-8888-8888-888888888002',
    tenantId: demoTenantId,
    siteId: downtownSiteId,
    cameraId: '66666666-6666-6666-6666-666666666608',
    score: 0.83,
    sceneTimestamp: iso(-13 * 60_000),
    sceneCaption: 'Multiple people in lounge corridor',
    sceneDescription:
      'The camera sees people seated in a lounge and another person walking along the corridor.',
    vlmModelId: 'qwen2.5-vl-3b',
    vlmModelVersion: 'qwen2.5-vl-3b@edge-demo',
    stage: 'alarm-burst',
  },
];

function isGuid(value: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value);
}

function searchLimit(raw: unknown): number {
  if (typeof raw !== 'number' || !Number.isFinite(raw) || raw <= 0) return 20;
  return Math.min(Math.trunc(raw), 100);
}

function minScore(raw: unknown): number {
  if (typeof raw !== 'number' || !Number.isFinite(raw)) return 0.25;
  if (raw < 0) return 0;
  if (raw > 1) return 1;
  return raw;
}

function bodyQuery(body: SemanticSearchBody): string {
  return typeof body.query === 'string' ? body.query.trim().toLowerCase() : '';
}

function matchesSemanticFilter(
  item: { siteId: string; cameraId: string; score: number },
  body: SemanticSearchBody,
  haystack: string,
): boolean {
  const query = bodyQuery(body);
  if (!query) return false;
  if (body.siteId && item.siteId !== body.siteId) return false;
  if (body.cameraId && item.cameraId !== body.cameraId) return false;
  if (item.score < minScore(body.minScore)) return false;

  const terms = query.split(/\s+/).filter(Boolean);
  return terms.length === 0 || terms.some((term) => haystack.toLowerCase().includes(term));
}

// =====================================================================
// SSP Portal + Admin Console fixtures (Sub-agent extension)
// =====================================================================
// TODO(openapi): the SSP/Admin shapes below are not yet in the central
// .NET 10 OpenAPI doc. Treat as forward-compatible drafts; field names
// match the planned spec so swap-out is mechanical.

interface SspSummary {
  id: SspId;
  name: string;
  status: 'active' | 'suspended' | 'decommissioned' | 'provisioning';
  billingPlan?: string;
  contractStartUtc?: string;
  ssuCount: number;
  siteCount: number;
  edgeOnline: number;
  edgeTotal: number;
  slaAttainment30d: number;
  contactEmail: string;
  emirates: string[];
  createdAt: string;
}

interface SsuSummary {
  id: SsuId;
  name: string;
  sspId: SspId;
  legalName?: string;
  billingAddress?: string;
  contractStartUtc?: string;
  sspName: string;
  status: 'active' | 'suspended' | 'provisioning' | 'decommissioned';
  siteCount: number;
  contactEmail: string;
  billingPlan: string;
  createdAt: string;
}

interface BillingInvoice {
  id: string;
  sspId: SspId;
  periodStart: string;
  periodEnd: string;
  status: 'draft' | 'issued' | 'paid' | 'overdue';
  totalAed: number;
  lineItems: Array<{
    id: string;
    description: string;
    quantity: number;
    unitAed: number;
    subtotalAed: number;
  }>;
  slaCreditsAed: number;
  issuedAt?: string;
}

interface ReportRun {
  id: string;
  catalogId: string;
  name: string;
  ranAt: string;
  ranBy: string;
  status: 'queued' | 'running' | 'succeeded' | 'failed';
  format: 'pdf' | 'csv' | 'xlsx';
  signedUrl?: string;
}

interface SopVersion {
  version: number;
  body: string;
  updatedAt: string;
  updatedBy: string;
  signedBy?: string;
  approvedAt?: string;
}

interface SopDocument {
  id: string;
  title: string;
  tier: 1 | 2 | 3 | 4 | 5;
  category: 'alarm-response' | 'dispatch' | 'escalation' | 'post-incident';
  version: number;
  updatedAt: string;
  updatedBy: string;
  body: string;
  publishedToSsus: number;
  history?: SopVersion[];
  signedBy?: string;
  approvedAt?: string;
}

/**
 * Phase B B5: mirrors central <c>WatchlistResponse</c> + <c>WatchlistEntryResponse</c>.
 * The MSW handlers mount on the real central paths so the admin-console dev
 * loop exercises the real wire contract (route shapes, audit fields, elevated
 * gating) without a real backend.
 */
type WatchlistType = 'Face' | 'Plate' | 'ReIdPerson' | 'ReIdVehicle';
type WatchlistTier = 'Informational' | 'Escalation' | 'Critical';
type WatchlistStatus = 'Active' | 'Disabled';
type WatchlistOwnerType = 'SiraGlobal' | 'SspPrivate' | 'SsuPrivate';
type WatchlistEntryCategory = 'Banned' | 'Vip' | 'Investigation' | 'Staff';
type WatchlistEntryStatus = 'Active' | 'Expired' | 'Removed';

interface Watchlist {
  watchlistId: string;
  ownerType: WatchlistOwnerType;
  ownerId: string | null;
  name: string;
  description: string;
  type: WatchlistType;
  tier: WatchlistTier;
  retentionDays: number;
  status: WatchlistStatus;
  createdAtUtc: string;
  createdByUserId: string;
}

interface WatchlistEntry {
  entryId: string;
  watchlistId: string;
  type: WatchlistType;
  label: string;
  category: WatchlistEntryCategory;
  dpiaReference: string;
  consentTimestamp: string | null;
  plateIdentifier: string | null;
  plateRegion: string | null;
  hasEmbedding: boolean;
  enrolledAtUtc: string;
  enrolledByUserId: string;
  expiresAtUtc: string | null;
  status: WatchlistEntryStatus;
}

interface ModelPin {
  sspId: SspId;
  sspName: string;
  modelId: string;
  pinnedVersion: string;
  pinnedAt: string;
  pinnedBy: string;
  reason: string;
}

type MockModelStatus = 'Draft' | 'Active' | 'Retired';
type MockModelCohort = 'None' | 'Canary' | 'Staging' | 'Prod';
const MOCK_MODEL_FAMILY_PATTERN = /^[a-z0-9][a-z0-9-]{0,62}[a-z0-9]?$/;
const MOCK_MODEL_SEMVER_PATTERN = /^\d+\.\d+\.\d+(-[0-9A-Za-z.-]+)?(\+[0-9A-Za-z.-]+)?$/;

interface MockModelVersion {
  versionId: string;
  modelId: string;
  semVer: string;
  artifactRef: string;
  artifactHash: string | null;
  cohort: MockModelCohort;
  createdByActorId: string;
  createdAtUtc: string;
  promotedAtUtc: string | null;
  lastPromotedByActorId: string | null;
}

interface MockRegistryModel {
  modelId: string;
  name: string;
  family: string;
  architecture: string;
  intent: string | null;
  status: MockModelStatus;
  currentVersionId: string | null;
  createdAtUtc: string;
  retiredAtUtc: string | null;
  createdByActorId: string;
  versions: MockModelVersion[];
}

async function readMockJsonRecord(request: Request): Promise<Record<string, unknown> | null> {
  let value: unknown;
  try {
    value = await request.json();
  } catch {
    return null;
  }
  return typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function isNonEmptyMockString(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}

function isBoundedMockString(value: unknown, maximumLength: number): value is string {
  return isNonEmptyMockString(value) && value.length <= maximumLength;
}

function isOptionalBoundedMockString(
  value: unknown,
  maximumLength: number,
): value is string | null {
  return value === null || (typeof value === 'string' && value.length <= maximumLength);
}

function isMockPromotionCohort(value: unknown): value is Exclude<MockModelCohort, 'None'> {
  return value === 'Canary' || value === 'Staging' || value === 'Prod';
}

/**
 * Wire-shape mirror of `CGuard.Modules.Admin.Contracts.Dtos.AuditActionResponse`
 * (the `/v1/admin/audit` list + drill-down DTO), camelCase per the central API's
 * `JsonSerializerDefaults.Web`. Diffs are carried as JSON *strings*
 * (`beforeStateJson` / `afterStateJson`) exactly as the real contract does; the
 * client parses them view-side. The former shape here (actor email, actorRole,
 * sspName, payloadHash, `outcome`) was fictional and never matched the API.
 */
interface MockAuditAction {
  auditId: string;
  actorUserId: string | null;
  actorServiceAccountId: string | null;
  actorIpAddress: string;
  occurredAtUtc: string;
  actionType: string;
  targetType: string;
  targetId: string;
  beforeStateJson: string | null;
  afterStateJson: string | null;
  outcomeStatus: string;
  correlationId: string;
}

/** Identity SessionResponse (not in the OpenAPI document). */
interface SessionResponse {
  sessionId: string;
  userId: string;
  keycloakSubject: string;
  startedAtUtc: string;
  lastSeenAtUtc: string;
  ipAddress: string;
  userAgent: string;
}

interface RolePermissionResponse {
  permissionId: string;
  roleName: string;
  permissionKey: string;
  resourceType: string;
  action: string;
  effect: string;
  requiredLoa: number;
  scopeRestriction: string | null;
  createdAtUtc: string;
}

interface OtaBundle {
  id: string;
  version: string;
  family: 'edge' | 'k3s' | 'os';
  publishedAt: string;
  publishedBy: string;
  cohort: 'canary' | 'early' | 'stable' | 'lagging';
  cosignVerified: boolean;
  rolloutPct: number;
  autoPaused: boolean;
}

const ssps: SspSummary[] = [
  {
    id: 'ssp-acme' as SspId,
    name: 'Acme Security',
    status: 'active',
    ssuCount: 12,
    siteCount: 87,
    edgeOnline: 84,
    edgeTotal: 87,
    slaAttainment30d: 0.987,
    contactEmail: 'ops@acme-security.ae',
    emirates: ['Dubai', 'Sharjah'],
    createdAt: iso(-365 * 24 * 60 * 60 * 1000),
  },
  {
    id: 'ssp-secure' as SspId,
    name: 'SecureCo',
    status: 'active',
    ssuCount: 7,
    siteCount: 41,
    edgeOnline: 39,
    edgeTotal: 41,
    slaAttainment30d: 0.962,
    contactEmail: 'noc@secureco.ae',
    emirates: ['Sharjah', 'Ajman'],
    createdAt: iso(-200 * 24 * 60 * 60 * 1000),
  },
  {
    id: 'ssp-sentry' as SspId,
    name: 'Sentry Gulf',
    status: 'provisioning',
    ssuCount: 0,
    siteCount: 0,
    edgeOnline: 0,
    edgeTotal: 0,
    slaAttainment30d: 0,
    contactEmail: 'admin@sentrygulf.ae',
    emirates: ['Abu Dhabi'],
    createdAt: iso(-3 * 24 * 60 * 60 * 1000),
  },
];

const ssus: SsuSummary[] = [
  {
    id: 'ssu-marina' as SsuId,
    name: 'Marina Holdings',
    sspId: 'ssp-acme' as SspId,
    sspName: 'Acme Security',
    status: 'active',
    siteCount: 4,
    contactEmail: 'security@marina-holdings.ae',
    billingPlan: 'Enterprise',
    createdAt: iso(-180 * 24 * 60 * 60 * 1000),
  },
  {
    id: 'ssu-downtown' as SsuId,
    name: 'Downtown JV',
    sspId: 'ssp-acme' as SspId,
    sspName: 'Acme Security',
    status: 'active',
    siteCount: 8,
    contactEmail: 'cso@downtown-jv.ae',
    billingPlan: 'Enterprise',
    createdAt: iso(-150 * 24 * 60 * 60 * 1000),
  },
  {
    id: 'ssu-yard' as SsuId,
    name: 'Yard Logistics',
    sspId: 'ssp-secure' as SspId,
    sspName: 'SecureCo',
    status: 'active',
    siteCount: 2,
    contactEmail: 'ops@yardlogistics.ae',
    billingPlan: 'Standard',
    createdAt: iso(-90 * 24 * 60 * 60 * 1000),
  },
];

function centralSspId(id: SspId): string {
  return CENTRAL_SSP_IDS[id as keyof typeof CENTRAL_SSP_IDS] ?? id;
}

function centralSsuId(id: SsuId): string {
  return CENTRAL_SSU_IDS[id as keyof typeof CENTRAL_SSU_IDS] ?? id;
}

function fixtureSspId(id: string): SspId {
  const entry = Object.entries(CENTRAL_SSP_IDS).find(([, centralId]) => centralId === id);
  return (entry?.[0] ?? id) as SspId;
}

function centralSspStatus(status: SspSummary['status']): string {
  switch (status) {
    case 'suspended':
      return 'Suspended';
    case 'decommissioned':
      return 'Decommissioned';
    case 'active':
    case 'provisioning':
      return 'Active';
  }
}

function centralSsuStatus(status: SsuSummary['status']): string {
  switch (status) {
    case 'suspended':
      return 'Suspended';
    case 'decommissioned':
      return 'Offboarded';
    case 'active':
    case 'provisioning':
      return 'Active';
  }
}

function toCentralSsp(ssp: SspSummary) {
  return {
    sspId: centralSspId(ssp.id),
    name: ssp.name,
    contactEmail: ssp.contactEmail,
    status: centralSspStatus(ssp.status),
    billingPlan: ssp.billingPlan ?? (ssp.id === 'ssp-acme' ? 'Enterprise' : 'Standard'),
    contractStartUtc: ssp.contractStartUtc ?? ssp.createdAt,
    createdAtUtc: ssp.createdAt,
    suspendedAtUtc: null,
    decommissionedAtUtc: null,
    suspensionReason: null,
    ssuCount: ssp.ssuCount,
    siteCount: ssp.siteCount,
    edgeOnline: ssp.edgeOnline,
    edgeTotal: ssp.edgeTotal,
    slaAttainment30d: ssp.slaAttainment30d,
  };
}

function toCentralSsu(ssu: SsuSummary) {
  return {
    ssuId: centralSsuId(ssu.id),
    owningSspId: centralSspId(ssu.sspId),
    legalName: ssu.legalName ?? ssu.name,
    tradeName: ssu.name,
    contactEmail: ssu.contactEmail,
    billingAddress: ssu.billingAddress ?? 'Dubai, United Arab Emirates',
    status: centralSsuStatus(ssu.status),
    contractStartUtc: ssu.contractStartUtc ?? ssu.createdAt,
    createdAtUtc: ssu.createdAt,
    offboardedAtUtc: null,
  };
}

const invoices: BillingInvoice[] = [
  {
    id: 'inv-2026-04-acme',
    sspId: 'ssp-acme' as SspId,
    periodStart: iso(-30 * 24 * 60 * 60 * 1000),
    periodEnd: iso(0),
    status: 'draft',
    totalAed: 18_420,
    lineItems: [
      {
        id: 'li-1',
        description: 'Sites · standard tier × 80',
        quantity: 80,
        unitAed: 200,
        subtotalAed: 16_000,
      },
      {
        id: 'li-2',
        description: 'Sites · premium tier × 7',
        quantity: 7,
        unitAed: 380,
        subtotalAed: 2_660,
      },
      {
        id: 'li-3',
        description: 'SLA credits',
        quantity: 1,
        unitAed: -240,
        subtotalAed: -240,
      },
    ],
    slaCreditsAed: -240,
  },
  {
    id: 'inv-2026-03-acme',
    sspId: 'ssp-acme' as SspId,
    periodStart: iso(-60 * 24 * 60 * 60 * 1000),
    periodEnd: iso(-30 * 24 * 60 * 60 * 1000),
    status: 'paid',
    totalAed: 18_240,
    lineItems: [
      {
        id: 'li-1',
        description: 'Sites · standard tier × 80',
        quantity: 80,
        unitAed: 200,
        subtotalAed: 16_000,
      },
      {
        id: 'li-2',
        description: 'Sites · premium tier × 7',
        quantity: 7,
        unitAed: 380,
        subtotalAed: 2_660,
      },
      {
        id: 'li-3',
        description: 'SLA credits',
        quantity: 1,
        unitAed: -420,
        subtotalAed: -420,
      },
    ],
    slaCreditsAed: -420,
    issuedAt: iso(-29 * 24 * 60 * 60 * 1000),
  },
];

const reportRuns: ReportRun[] = [
  {
    id: 'rr-001',
    catalogId: 'sla-monthly',
    name: 'SLA attainment · monthly',
    ranAt: iso(-2 * 24 * 60 * 60 * 1000),
    ranBy: 'samira.k@acme-security.ae',
    status: 'succeeded',
    format: 'pdf',
    signedUrl: '/exports/rr-001.pdf?sig=mock',
  },
  {
    id: 'rr-002',
    catalogId: 'incidents-by-tier',
    name: 'Incidents by tier · 30d',
    ranAt: iso(-1 * 24 * 60 * 60 * 1000),
    ranBy: 'samira.k@acme-security.ae',
    status: 'succeeded',
    format: 'csv',
    signedUrl: '/exports/rr-002.csv?sig=mock',
  },
  {
    id: 'rr-003',
    catalogId: 'edge-fleet-health',
    name: 'Edge fleet health · weekly',
    ranAt: iso(-30 * 60_000),
    ranBy: 'noc@acme-security.ae',
    status: 'running',
    format: 'pdf',
  },
];

const sopHistoryFor = (current: number, baseBody: string): SopVersion[] =>
  Array.from({ length: current }, (_, i) => {
    const v = i + 1;
    const base: SopVersion = {
      version: v,
      body: `${baseBody}\n\n_Revision ${v}_`,
      updatedAt: iso(-(current - i) * 14 * 24 * 60 * 60 * 1000),
      updatedBy: v % 2 === 0 ? 'platform-admin@uxe.ai' : 'auditor@uxe.ai',
    };
    return v < current
      ? {
          ...base,
          signedBy: 'platform-admin@uxe.ai',
          approvedAt: iso(-(current - i) * 14 * 24 * 60 * 60 * 1000),
        }
      : base;
  });

const sopBody1 = `# Tier 1 — Imminent threat response

When a Tier 1 verdict is raised the operator MUST:
1. Acknowledge within 15 seconds.
2. Triple-check panel signal, AI verdict, and live video.
3. Dispatch nearest patrol via radio.
4. Initiate SIRA escalation if confirmed within 60 seconds.

Reference: SIRA-OP-T1-2026.`;
const sopBody2 = `# Tier 2 — Confirmed intrusion

On a Tier 2 verdict the operator must verify panel + AI agreement and dispatch the on-call patrol.`;
const sopBody3 = `# Tier 3 — Probable nuisance

Tier 3 events are dispatched as informational only; the operator logs and continues monitoring.`;
const sopBodyDispatch = `# Dispatch protocol — patrol & police escalation

Routes a verified incident to the nearest patrol; escalates to police when:
- Tier 1 confirmed for 60 s, OR
- Operator manually marks "armed response required".`;
const sopBodyEsc = `# Escalation matrix

T1 → SSP duty manager + SIRA + police.
T2 → SSP duty manager.
T3 → SSP NOC desk only.`;
const sopBodyPost = `# Post-incident — documentation & retention

Within 24 hours of close the operator must complete the disposition note, attach evidence clips, and the incident enters the 90-day retention window.`;

const sops: SopDocument[] = [
  {
    id: 'sop-tier1',
    title: 'Tier 1 — Imminent threat response',
    tier: 1,
    category: 'alarm-response',
    version: 7,
    updatedAt: iso(-14 * 24 * 60 * 60 * 1000),
    updatedBy: 'platform-admin@uxe.ai',
    body: sopBody1,
    publishedToSsus: 19,
    history: sopHistoryFor(7, sopBody1),
    signedBy: 'platform-admin@uxe.ai',
    approvedAt: iso(-14 * 24 * 60 * 60 * 1000),
  },
  {
    id: 'sop-tier2',
    title: 'Tier 2 — Confirmed intrusion',
    tier: 2,
    category: 'alarm-response',
    version: 5,
    updatedAt: iso(-21 * 24 * 60 * 60 * 1000),
    updatedBy: 'platform-admin@uxe.ai',
    body: sopBody2,
    publishedToSsus: 19,
    history: sopHistoryFor(5, sopBody2),
    signedBy: 'platform-admin@uxe.ai',
    approvedAt: iso(-21 * 24 * 60 * 60 * 1000),
  },
  {
    id: 'sop-tier3',
    title: 'Tier 3 — Probable nuisance',
    tier: 3,
    category: 'alarm-response',
    version: 3,
    updatedAt: iso(-45 * 24 * 60 * 60 * 1000),
    updatedBy: 'platform-admin@uxe.ai',
    body: sopBody3,
    publishedToSsus: 19,
    history: sopHistoryFor(3, sopBody3),
    signedBy: 'platform-admin@uxe.ai',
    approvedAt: iso(-45 * 24 * 60 * 60 * 1000),
  },
  {
    id: 'sop-dispatch',
    title: 'Patrol dispatch + police escalation',
    tier: 2,
    category: 'dispatch',
    version: 4,
    updatedAt: iso(-60 * 24 * 60 * 60 * 1000),
    updatedBy: 'platform-admin@uxe.ai',
    body: sopBodyDispatch,
    publishedToSsus: 19,
    history: sopHistoryFor(4, sopBodyDispatch),
    signedBy: 'platform-admin@uxe.ai',
    approvedAt: iso(-60 * 24 * 60 * 60 * 1000),
  },
  {
    id: 'sop-escalation',
    title: 'Multi-tier escalation matrix',
    tier: 1,
    category: 'escalation',
    version: 2,
    updatedAt: iso(-90 * 24 * 60 * 60 * 1000),
    updatedBy: 'platform-admin@uxe.ai',
    body: sopBodyEsc,
    publishedToSsus: 19,
    history: sopHistoryFor(2, sopBodyEsc),
    signedBy: 'platform-admin@uxe.ai',
    approvedAt: iso(-90 * 24 * 60 * 60 * 1000),
  },
  {
    id: 'sop-post-incident',
    title: 'Post-incident documentation & retention',
    tier: 4,
    category: 'post-incident',
    version: 6,
    updatedAt: iso(-30 * 24 * 60 * 60 * 1000),
    updatedBy: 'auditor@uxe.ai',
    body: sopBodyPost,
    publishedToSsus: 19,
    history: sopHistoryFor(6, sopBodyPost),
    signedBy: 'platform-admin@uxe.ai',
    approvedAt: iso(-30 * 24 * 60 * 60 * 1000),
  },
];

const SIRA_BANNED_FACE_WL_ID = 'cd6f5f1d-8e2a-7a8e-9c10-9c2cf001face';
const SIRA_BANNED_PLATE_WL_ID = 'cd6f5f1d-8e2a-7a8e-9c10-9c2cf002plat';
const ACME_VIP_FACE_WL_ID = 'cd6f5f1d-8e2a-7a8e-9c10-9c2cf003vips';

const watchlistsCatalog: Watchlist[] = [
  {
    watchlistId: SIRA_BANNED_FACE_WL_ID,
    ownerType: 'SiraGlobal',
    ownerId: null,
    name: 'SIRA Banned (face)',
    description: 'SIRA-global banned-persons face watchlist',
    type: 'Face',
    tier: 'Critical',
    retentionDays: 365,
    status: 'Active',
    createdAtUtc: iso(-90 * 24 * 60 * 60 * 1000),
    createdByUserId: '00000000-0000-0000-0000-000000000001',
  },
  {
    watchlistId: SIRA_BANNED_PLATE_WL_ID,
    ownerType: 'SiraGlobal',
    ownerId: null,
    name: 'SIRA Banned (plate)',
    description: 'SIRA-global banned-vehicle plate watchlist',
    type: 'Plate',
    tier: 'Critical',
    retentionDays: 365,
    status: 'Active',
    createdAtUtc: iso(-90 * 24 * 60 * 60 * 1000),
    createdByUserId: '00000000-0000-0000-0000-000000000001',
  },
  {
    watchlistId: ACME_VIP_FACE_WL_ID,
    ownerType: 'SspPrivate',
    ownerId: 'ssp-acme',
    name: 'Acme VIPs (face)',
    description: 'SSP-private VIP face list',
    type: 'Face',
    tier: 'Informational',
    retentionDays: 730,
    status: 'Active',
    createdAtUtc: iso(-30 * 24 * 60 * 60 * 1000),
    createdByUserId: '00000000-0000-0000-0000-000000000002',
  },
];

const watchlistEntries: WatchlistEntry[] = [
  {
    entryId: 'a13b89bc-fd1e-4001-9001-0001face00001',
    watchlistId: SIRA_BANNED_FACE_WL_ID,
    type: 'Face',
    label: 'Marina gate visitor',
    category: 'Investigation',
    dpiaReference: 'DPIA-2026-014',
    consentTimestamp: iso(-60 * 24 * 60 * 60 * 1000),
    plateIdentifier: null,
    plateRegion: null,
    hasEmbedding: true,
    enrolledAtUtc: iso(-60 * 24 * 60 * 60 * 1000),
    enrolledByUserId: '00000000-0000-0000-0000-000000000001',
    expiresAtUtc: iso(305 * 24 * 60 * 60 * 1000),
    status: 'Active',
  },
  {
    entryId: 'a13b89bc-fd1e-4002-9002-0002plate0001',
    watchlistId: SIRA_BANNED_PLATE_WL_ID,
    type: 'Plate',
    label: 'DXB A 12345',
    category: 'Banned',
    dpiaReference: 'DPIA-2026-021',
    consentTimestamp: iso(-30 * 24 * 60 * 60 * 1000),
    plateIdentifier: 'A12345',
    plateRegion: 'AE-DXB',
    hasEmbedding: false,
    enrolledAtUtc: iso(-30 * 24 * 60 * 60 * 1000),
    enrolledByUserId: '00000000-0000-0000-0000-000000000001',
    expiresAtUtc: null,
    status: 'Active',
  },
  {
    entryId: 'a13b89bc-fd1e-4003-9003-0003face00002',
    watchlistId: ACME_VIP_FACE_WL_ID,
    type: 'Face',
    label: 'VIP-Tenant-Marina-04',
    category: 'Vip',
    dpiaReference: 'DPIA-2026-077',
    consentTimestamp: iso(-15 * 24 * 60 * 60 * 1000),
    plateIdentifier: null,
    plateRegion: null,
    hasEmbedding: true,
    enrolledAtUtc: iso(-15 * 24 * 60 * 60 * 1000),
    enrolledByUserId: '00000000-0000-0000-0000-000000000002',
    expiresAtUtc: null,
    status: 'Active',
  },
];

const MOCK_MODEL_ACTOR_ID = '00000000-0000-4000-8000-000000000001';
const DETECTION_MODEL_ID = '10000000-0000-4000-8000-000000000001';
const DETECTION_PROD_VERSION_ID = '20000000-0000-4000-8000-000000000001';
const DETECTION_CANARY_VERSION_ID = '20000000-0000-4000-8000-000000000002';

const modelPins: ModelPin[] = [
  {
    sspId: '30000000-0000-4000-8000-000000000001' as SspId,
    sspName: 'SecureCo',
    modelId: DETECTION_MODEL_ID,
    pinnedVersion: '2026.04.0',
    pinnedAt: iso(-12 * 24 * 60 * 60 * 1000),
    pinnedBy: MOCK_MODEL_ACTOR_ID,
    reason: 'Customer requested freeze pending SOC2 re-cert',
  },
];

const models: MockRegistryModel[] = [
  {
    modelId: DETECTION_MODEL_ID,
    name: 'YOLOv9-s detection',
    family: 'person-vehicle-detection',
    architecture: 'yolov9-s',
    intent: 'Person and vehicle detection on edge',
    status: 'Active',
    currentVersionId: DETECTION_PROD_VERSION_ID,
    createdAtUtc: iso(-90 * 24 * 60 * 60 * 1000),
    retiredAtUtc: null,
    createdByActorId: MOCK_MODEL_ACTOR_ID,
    versions: [
      {
        versionId: DETECTION_PROD_VERSION_ID,
        modelId: DETECTION_MODEL_ID,
        semVer: '2026.04.0',
        artifactRef: 'registry.cguard.example/models/yolov9-s:2026.04.0',
        artifactHash: 'sha256:01ab4fc79e2',
        cohort: 'Prod',
        createdByActorId: MOCK_MODEL_ACTOR_ID,
        createdAtUtc: iso(-45 * 24 * 60 * 60 * 1000),
        promotedAtUtc: iso(-30 * 24 * 60 * 60 * 1000),
        lastPromotedByActorId: MOCK_MODEL_ACTOR_ID,
      },
      {
        versionId: DETECTION_CANARY_VERSION_ID,
        modelId: DETECTION_MODEL_ID,
        semVer: '2026.04.1-rc',
        artifactRef: 'registry.cguard.example/models/yolov9-s:2026.04.1-rc',
        artifactHash: 'sha256:cc18a30bf91',
        cohort: 'Canary',
        createdByActorId: MOCK_MODEL_ACTOR_ID,
        createdAtUtc: iso(-2 * 24 * 60 * 60 * 1000),
        promotedAtUtc: iso(-24 * 60 * 60 * 1000),
        lastPromotedByActorId: MOCK_MODEL_ACTOR_ID,
      },
    ],
  },
  {
    modelId: '10000000-0000-4000-8000-000000000002',
    name: 'EdgeFace recognition',
    family: 'face-recognition',
    architecture: 'edgeface-edge',
    intent: 'Watchlist face identification',
    status: 'Draft',
    currentVersionId: null,
    createdAtUtc: iso(-24 * 60 * 60 * 1000),
    retiredAtUtc: null,
    createdByActorId: MOCK_MODEL_ACTOR_ID,
    versions: [],
  },
];

// 1.2k synthetic audit actions mirroring the real AuditActionResponse contract;
// the admin-console viewer virtualizes them. Action types + descriptions are the
// canonical `AuditActionType` enum (Admin.Domain.AuditActions.AuditActionType).
const AUDIT_ACTION_TYPE_CATALOG: ReadonlyArray<{
  name: string;
  description: string;
}> = [
  {
    name: 'Auth',
    description: 'Authentication event (login, logout, MFA challenge).',
  },
  {
    name: 'Authz',
    description: 'Authorization decision (allow/deny against a policy).',
  },
  {
    name: 'PrivilegedWrite',
    description: 'State-changing privileged write (RBAC change, watchlist edit, etc.).',
  },
  {
    name: 'DataAccess',
    description: 'Read of sensitive data (biometric, video clip, audit log itself).',
  },
  {
    name: 'ConfigChange',
    description: 'Configuration change (per-tenant or global setting).',
  },
];
const AUDIT_TARGET_TYPES = [
  'User',
  'Watchlist',
  'Config',
  'SiraBundle',
  'Site',
  'AuditLog',
] as const;
// A small pool of deterministic actor GUIDs so `/actors` groups into a few rows.
const AUDIT_ACTOR_IDS = [
  '11111111-1111-4111-8111-111111111111',
  '22222222-2222-4222-8222-222222222222',
  '33333333-3333-4333-8333-333333333333',
] as const;
const AUDIT_SERVICE_ACCOUNT_ID = '99999999-9999-4999-8999-999999999999';
const AUDIT_OUTCOMES = ['Success', 'Success', 'Denied', 'Error'] as const;

/** Deterministic v4-shaped GUID for a mock audit row. */
function mockAuditGuid(i: number): string {
  return `00000000-0000-4000-8000-${(i + 1).toString(16).padStart(12, '0')}`;
}

const audit: MockAuditAction[] = Array.from({ length: 1200 }, (_, i): MockAuditAction => {
  const actionType =
    AUDIT_ACTION_TYPE_CATALOG[i % AUDIT_ACTION_TYPE_CATALOG.length]?.name ?? 'Auth';
  const targetType = AUDIT_TARGET_TYPES[i % AUDIT_TARGET_TYPES.length] ?? 'User';
  const outcomeStatus = AUDIT_OUTCOMES[i % AUDIT_OUTCOMES.length] ?? 'Success';
  // Every 5th row is a service-account actor (no actorUserId), matching the real
  // contract where exactly one of the two actor ids is populated.
  const isService = i % 5 === 0;
  const actorUserId = isService ? null : (AUDIT_ACTOR_IDS[i % AUDIT_ACTOR_IDS.length] ?? null);
  const actorServiceAccountId = isService ? AUDIT_SERVICE_ACCOUNT_ID : null;
  // Provide before/after diffs (as JSON strings) for the state-changing kinds.
  let beforeStateJson: string | null = null;
  let afterStateJson: string | null = null;
  if (actionType === 'ConfigChange') {
    beforeStateJson = JSON.stringify({ value: `0.${(i * 13) % 100}` });
    afterStateJson = JSON.stringify({ value: `0.${(i * 17) % 100}` });
  } else if (actionType === 'PrivilegedWrite') {
    beforeStateJson = JSON.stringify({ status: 'active' });
    afterStateJson = JSON.stringify({
      status: i % 17 === 0 ? 'suspended' : 'active',
    });
  }
  return {
    auditId: mockAuditGuid(i),
    actorUserId,
    actorServiceAccountId,
    actorIpAddress: `10.0.${(i % 254) + 1}.${(i * 7) % 254}`,
    occurredAtUtc: iso(-i * 60_000),
    actionType,
    targetType,
    targetId: `tgt-${i + 1}`,
    beforeStateJson,
    afterStateJson,
    outcomeStatus,
    correlationId: (i * 1_000_003).toString(16).padStart(16, '0'),
  };
});

// LegalHoldResponse rows: one target type, the first target id as `scope`, the coverage in `reason`.
const legalHold = (
  n: number,
  hold: Pick<LegalHoldResponse, 'caseRef' | 'reason' | 'affectsTargetType' | 'affectedCount'> & {
    targetId: string;
    imposedDaysAgo: number;
    lift?: { daysAgo: number; reason: string };
  },
): LegalHoldResponse => ({
  holdId: `71000000-0000-4000-8000-${String(n).padStart(12, '0')}`,
  caseRef: hold.caseRef,
  requestedByUserId: mockUserId(1),
  reason: hold.reason,
  affectsTargetType: hold.affectsTargetType,
  affectsTargetIds: [hold.targetId],
  scope: { type: hold.affectsTargetType, id: hold.targetId },
  affectedCount: hold.affectedCount,
  imposedAtUtc: iso(-hold.imposedDaysAgo * DAY_MS),
  liftedAtUtc: hold.lift ? iso(-hold.lift.daysAgo * DAY_MS) : null,
  isActive: !hold.lift,
  status: hold.lift ? 'lifted' : 'active',
  liftContext: hold.lift
    ? {
        liftedAtUtc: iso(-hold.lift.daysAgo * DAY_MS),
        liftedByUserId: mockUserId(1),
        reason: hold.lift.reason,
      }
    : null,
});

const legalHolds: LegalHoldResponse[] = [
  legalHold(1, {
    caseRef: 'DPP-2026-Q2-014',
    reason: 'Dubai Police criminal investigation (incidents INC-1001)',
    affectsTargetType: 'site',
    targetId: marinaSiteId,
    affectedCount: 23,
    imposedDaysAgo: 7,
  }),
  legalHold(2, {
    caseRef: 'SIRA-2026-AUD-018',
    reason: 'SIRA audit retention extension (range 1 to 15 April 2026)',
    affectsTargetType: 'site',
    targetId: downtownSiteId,
    affectedCount: 412,
    imposedDaysAgo: 12,
  }),
  legalHold(3, {
    caseRef: 'DPP-2026-Q1-090',
    reason: 'Theft claim at the service yard (incidents INC-1003)',
    affectsTargetType: 'ssu',
    targetId: CENTRAL_SSU_IDS['ssu-yard'],
    affectedCount: 4,
    imposedDaysAgo: 30,
    lift: { daysAgo: 2, reason: 'Investigation closed; retention defaults restored.' },
  }),
];

// Users and sessions in the identity UserResponse / SessionResponse shapes. User 1 is the
// signed-in identityMe user.

function platformUser(
  n: number,
  user: Pick<
    UserResponse,
    'email' | 'displayName' | 'role' | 'tenantTier' | 'sspId' | 'ssuId' | 'lastActiveUtc'
  >,
): UserResponse {
  return {
    userId: mockUserId(n),
    keycloakSubject: `20000000-0000-4000-8000-${String(n).padStart(12, '0')}`,
    preferredLanguage: 'en',
    activeSiteId: null,
    roles: user.role === null ? [] : [user.role],
    status: 'Active',
    createdAtUtc: iso(-120 * 24 * 60 * 60 * 1000),
    mfaEnrolled: true,
    adminBootstrapStatus: 'Accepted',
    bootstrapAttemptCount: 1,
    ...user,
  };
}

const platformUsers: UserResponse[] = [
  platformUser(1, {
    email: 'platform-admin@uxe.ai',
    displayName: 'UXE platform admin',
    role: 'sira-admin',
    tenantTier: 'Platform',
    sspId: null,
    ssuId: null,
    lastActiveUtc: iso(-5 * 60_000),
  }),
  platformUser(2, {
    email: 'auditor@uxe.ai',
    displayName: 'UXE auditor',
    role: 'audit-viewer',
    tenantTier: 'Platform',
    sspId: null,
    ssuId: null,
    lastActiveUtc: iso(-3 * 60 * 60 * 1000),
  }),
  platformUser(3, {
    email: 'samira.k@acme-security.ae',
    displayName: 'Samira K.',
    role: 'ssp-admin',
    tenantTier: 'Ssp',
    sspId: CENTRAL_SSP_IDS['ssp-acme'],
    ssuId: null,
    lastActiveUtc: iso(-30 * 60_000),
  }),
  platformUser(4, {
    email: 'noc@acme-security.ae',
    displayName: 'NOC desk',
    role: 'ssp-tech',
    tenantTier: 'Ssp',
    sspId: CENTRAL_SSP_IDS['ssp-acme'],
    ssuId: null,
    lastActiveUtc: iso(-2 * 60_000),
  }),
  platformUser(5, {
    email: 'marina.ops@acme-security.ae',
    displayName: 'Marina operations',
    role: 'ssu-admin',
    tenantTier: 'Ssu',
    sspId: CENTRAL_SSP_IDS['ssp-acme'],
    ssuId: CENTRAL_SSU_IDS['ssu-marina'],
    lastActiveUtc: iso(-12 * 60 * 60 * 1000),
  }),
];

const platformSessions: SessionResponse[] = [
  {
    sessionId: '30000000-0000-4000-8000-000000000001',
    userId: mockUserId(1),
    keycloakSubject: platformUsers[0]!.keycloakSubject,
    startedAtUtc: iso(-2 * 60 * 60 * 1000),
    lastSeenAtUtc: iso(-30_000),
    ipAddress: '10.40.0.18',
    userAgent: 'Chrome 134 / macOS',
  },
  {
    sessionId: '30000000-0000-4000-8000-000000000002',
    userId: mockUserId(2),
    keycloakSubject: platformUsers[1]!.keycloakSubject,
    startedAtUtc: iso(-90 * 60_000),
    lastSeenAtUtc: iso(-2 * 60_000),
    ipAddress: '10.40.0.22',
    userAgent: 'Firefox 132 / Linux',
  },
];

// ConfigurationResponse rows. A key can repeat across scopes; PUT addresses key + scope + scopeId.
const configuration = (
  n: number,
  configKey: string,
  valueJson: string,
  description: string,
  ageDays: number,
  scope: 'Global' | 'Ssp' = 'Global',
  scopeId: string | null = null,
): ConfigurationResponse => ({
  configurationId: `70000000-0000-4000-8000-${String(n).padStart(12, '0')}`,
  configKey,
  scope,
  scopeId,
  valueJson,
  description,
  editableByRoles: [],
  lastUpdatedByUserId: mockUserId(1),
  lastUpdatedAtUtc: iso(-ageDays * DAY_MS),
});

const configurationEntries: ConfigurationResponse[] = [
  configuration(1, 'incidents.tier1.autoDispatchSeconds', '15', 'Seconds before a Tier 1 incident is dispatched automatically.', 7),
  configuration(2, 'edge.heartbeat.timeoutSeconds', '60', 'Seconds without a heartbeat before an appliance counts as offline.', 30),
  configuration(3, 'recordings.retention.daysDefault', '31', 'Days that recordings are kept by default.', 90),
  configuration(4, 'retention.audit.years', '7', 'Years that audit records are kept.', 180),
  configuration(5, 'retention.evidence.legalHold.years', '10', 'Years that evidence under legal hold is kept.', 180),
  configuration(6, 'feature.aiAutoDispatch', 'true', 'Let AI detections dispatch incidents without an operator.', 3),
  configuration(7, 'feature.watchlistsAdvancedSearch', 'true', 'Show the advanced watchlist search.', 7),
  configuration(8, 'feature.commissioningPwaOffline', 'false', 'Let the commissioning app work offline.', 21),
  configuration(9, 'aiTier.capacity', '60', 'AI analysis capacity, in camera streams.', 2, 'Ssp', CENTRAL_SSP_IDS['ssp-acme']),
  configuration(10, 'aiTier.capacity', '40', 'AI analysis capacity, in camera streams.', 12, 'Ssp', CENTRAL_SSP_IDS['ssp-secure']),
  configuration(11, 'aiTier.capacity', '10', 'AI analysis capacity, in camera streams.', 1, 'Ssp', CENTRAL_SSP_IDS['ssp-sentry']),
];

// Paged, filtered watchlist entries: across watchlists, or within one when an id is given.
function watchlistEntryPage(url: URL, watchlistId?: string) {
  const page = Math.max(1, Number(url.searchParams.get('page') ?? 1));
  const pageSize = Math.min(200, Math.max(1, Number(url.searchParams.get('pageSize') ?? 50)));
  const type = url.searchParams.get('type');
  const category = url.searchParams.get('category');
  const status = url.searchParams.get('status');
  const needle = url.searchParams.get('labelSearch')?.toLowerCase();
  const view = watchlistEntries
    .filter(
      (e) =>
        (!watchlistId || e.watchlistId === watchlistId) &&
        (!type || e.type === type) &&
        (!category || e.category === category) &&
        (!status || e.status === status) &&
        (!needle || e.label.toLowerCase().includes(needle)),
    )
    .toSorted((a, b) => b.enrolledAtUtc.localeCompare(a.enrolledAtUtc));
  return {
    items: view.slice((page - 1) * pageSize, page * pageSize),
    page,
    pageSize,
    total: view.length,
  };
}

type PermissionIntegerQueryParameter =
  | { kind: 'missing' }
  | { kind: 'valid'; value: number }
  | { kind: 'invalid' };

function parsePermissionIntegerQueryParameter(
  searchParams: URLSearchParams,
  name: string,
): PermissionIntegerQueryParameter {
  const rawValue = searchParams.get(name);
  if (rawValue === null) return { kind: 'missing' };

  const text = rawValue.trim();
  if (!/^[+-]?\d+$/.test(text)) return { kind: 'invalid' };

  const value = Number(text);
  return Number.isInteger(value) && value >= -2_147_483_648 && value <= 2_147_483_647
    ? { kind: 'valid', value }
    : { kind: 'invalid' };
}

const rolePermissionCreatedAtUtc = '2026-07-22T08:00:00Z';
const rolePermissions: RolePermissionResponse[] = [
  {
    permissionId: '10000000-0000-4000-8000-000000000001',
    roleName: 'sira-admin',
    permissionKey: 'permissions.manage',
    resourceType: 'permissions',
    action: 'manage',
    effect: 'Allow',
    requiredLoa: 1,
    scopeRestriction: null,
    createdAtUtc: rolePermissionCreatedAtUtc,
  },
  {
    permissionId: '10000000-0000-4000-8000-000000000002',
    roleName: 'audit-viewer',
    permissionKey: 'audit.read',
    resourceType: 'audit',
    action: 'read',
    effect: 'Allow',
    requiredLoa: 1,
    scopeRestriction: null,
    createdAtUtc: rolePermissionCreatedAtUtc,
  },
  {
    permissionId: '10000000-0000-4000-8000-000000000003',
    roleName: 'audit-viewer',
    permissionKey: 'reports.read',
    resourceType: 'reports',
    action: 'read',
    effect: 'Allow',
    requiredLoa: 1,
    scopeRestriction: null,
    createdAtUtc: rolePermissionCreatedAtUtc,
  },
  {
    permissionId: '10000000-0000-4000-8000-000000000004',
    roleName: 'sira-admin',
    permissionKey: 'tenants.ssus.override',
    resourceType: 'tenants.ssus',
    action: 'override',
    effect: 'Allow',
    requiredLoa: 2,
    scopeRestriction: null,
    createdAtUtc: rolePermissionCreatedAtUtc,
  },
  {
    permissionId: '10000000-0000-4000-8000-000000000005',
    roleName: 'ssp-tech',
    permissionKey: 'sites.read',
    resourceType: 'sites',
    action: 'read',
    effect: 'Allow',
    requiredLoa: 1,
    scopeRestriction: null,
    createdAtUtc: rolePermissionCreatedAtUtc,
  },
  {
    permissionId: '10000000-0000-4000-8000-000000000006',
    roleName: 'operator',
    permissionKey: 'incidents.handle',
    resourceType: 'incidents',
    action: 'handle',
    effect: 'Allow',
    requiredLoa: 1,
    scopeRestriction: null,
    createdAtUtc: rolePermissionCreatedAtUtc,
  },
];

function workloadRelease(
  index: number,
  release: Pick<WorkloadReleaseSummary, 'version' | 'releaseId' | 'state' | 'rollbackRelease'>,
): WorkloadReleaseSummary {
  const ageMs = -(2 + index * 7) * 86_400_000;
  return {
    id: `0f6c1d2e-3a4b-4c5d-8e6f-${String(index + 1).padStart(12, '0')}`,
    refusalReason: null,
    refusalDetail: null,
    channel: 'commercial',
    manifestDigest: `sha256:${mockDigest(`${index}a`)}`,
    manifestArtifactDigest: `sha256:${mockDigest(`${index}b`)}`,
    gateRecordDigest: `sha256:${mockDigest(`${index}c`)}`,
    gateBlocker: null,
    replicationTriggerCount: 0,
    failedReplicationTriggerCount: 0,
    discoveredAtUtc: iso(ageMs),
    stateChangedAtUtc: iso(ageMs + 1_800_000),
    contentDeletedAtUtc: release.state === 'Retired' ? iso(ageMs + 86_400_000) : null,
    requiredBy: [],
    ...release,
  };
}

const workloadReleases: WorkloadReleaseSummary[] = [
  workloadRelease(0, {
    version: 'edge-2026-10-24',
    releaseId: 'edge-2026-10-24',
    state: 'Deployable',
    rollbackRelease: 'edge-2026-10-21',
  }),
  workloadRelease(1, {
    version: 'edge-2026-10-21',
    releaseId: 'edge-2026-10-21',
    state: 'Deployable',
    rollbackRelease: 'edge-2026-10-14',
  }),
  workloadRelease(2, {
    version: 'edge-2026-10-18-rc1',
    releaseId: null,
    state: 'Deployable',
    rollbackRelease: null,
  }),
  workloadRelease(3, {
    version: 'edge-2026-10-14',
    releaseId: 'edge-2026-10-14',
    state: 'Retired',
    rollbackRelease: null,
  }),
];

type MockProvisioningRelease = Pick<
  ProvisioningReleaseResponse,
  | 'releaseId'
  | 'channel'
  | 'version'
  | 'sourceCommitSha'
  | 'status'
  | 'createdAtUtc'
  | 'approvedAtUtc'
  | 'expiresAtUtc'
  | 'descriptorSha256'
  | 'descriptorSignatureLocation'
  | 'descriptorSignatureSha256'
  | 'revokedAtUtc'
  | 'revocationReason'
> & {
  artifacts: Omit<
    ProvisioningArtifactResponse,
    'signingIdentity' | 'signingIssuer' | 'transparencyLogLocation' | 'builderIdentity'
  >[];
};

function provisioningRelease(
  releaseId: string,
  version: string,
  sourceCommitSha: string,
  approvedDaysAgo: number,
  revocationReason: string | null = null,
): MockProvisioningRelease {
  const base = `/v1/provisioning/releases/${releaseId}/artifacts`;
  const artifact = (kind: string, mediaType: string, sizeBytes: number, seed: string) => ({
    kind,
    location: `${base}/${kind}/content`,
    mediaType,
    sizeBytes,
    sha256: mockDigest(`${seed}0`),
    signatureLocation: `${base}/${kind}/signature`,
    signatureSha256: mockDigest(`${seed}1`),
    sbomLocation: `${base}/${kind}/sbom`,
    sbomSha256: mockDigest(`${seed}2`),
    provenanceLocation: `${base}/${kind}/provenance`,
    provenanceSha256: mockDigest(`${seed}3`),
  });
  const approvedAtUtc = iso(-approvedDaysAgo * 86_400_000);
  return {
    releaseId,
    channel: 'commercial',
    version,
    sourceCommitSha,
    status: revocationReason ? 'Revoked' : 'Approved',
    createdAtUtc: approvedAtUtc,
    approvedAtUtc,
    expiresAtUtc: iso((90 - approvedDaysAgo) * 86_400_000),
    descriptorSha256: mockDigest(`${releaseId[0]}d`),
    descriptorSignatureLocation: `${base}/descriptor/signature`,
    descriptorSignatureSha256: mockDigest(`${releaseId[0]}e`),
    revokedAtUtc: revocationReason ? iso(-86_400_000) : null,
    revocationReason,
    artifacts: [
      artifact('Iso', 'application/x-iso9660-image', 2_147_483_648, 'e'),
      artifact('Kernel', 'application/octet-stream', 16_777_216, 'f'),
    ],
  };
}

const provisioningReleases: MockProvisioningRelease[] = [
  provisioningRelease(
    '6a1f0c3e-2b4d-4e5f-9a6b-7c8d9e0f1a2b',
    '2026.10.1',
    '8dd2c30f4b1e9a7c6d5e4f3a2b1c0d9e8f7a6b5c',
    3,
  ),
  provisioningRelease(
    '7b2e1d4f-3c5e-4f60-8b7c-8d9e0f1a2b3c',
    '2026.09.3',
    '576d26e1a2b3c4d5e6f708192a3b4c5d6e7f8091',
    26,
    'Superseded after a recorder driver regression.',
  ),
];

const otaBundles: OtaBundle[] = [
  {
    id: 'ota-edge-2026.04.24-canary',
    version: '2026.04.24',
    family: 'edge',
    publishedAt: iso(-1 * 24 * 60 * 60 * 1000),
    publishedBy: 'platform-admin@uxe.ai',
    cohort: 'canary',
    cosignVerified: true,
    rolloutPct: 5,
    autoPaused: false,
  },
  {
    id: 'ota-edge-2026.04.22-early',
    version: '2026.04.22',
    family: 'edge',
    publishedAt: iso(-3 * 24 * 60 * 60 * 1000),
    publishedBy: 'platform-admin@uxe.ai',
    cohort: 'early',
    cosignVerified: true,
    rolloutPct: 25,
    autoPaused: false,
  },
  {
    id: 'ota-edge-2026.04.18-stable',
    version: '2026.04.18',
    family: 'edge',
    publishedAt: iso(-7 * 24 * 60 * 60 * 1000),
    publishedBy: 'platform-admin@uxe.ai',
    cohort: 'stable',
    cosignVerified: true,
    rolloutPct: 100,
    autoPaused: false,
  },
];

/**
 * MSW handlers for `@cguard/api-client` — DEV / TEST ONLY.
 *
 * These intercept the orval-generated `/v1/...` calls so SPAs can boot with
 * realistic data before the central .NET API is reachable. They are gated
 * behind `import.meta.env.DEV` in every app's `main.tsx` so a production
 * build never starts MSW. Once a real central endpoint exists for a path
 * here, delete the corresponding handler — the orval client will hit the
 * real API automatically.
 *
 * Path prefix is `/v1/...` to match the real central. The legacy `/api/...`
 * tree (admin/ssp namespaces below) covers endpoints that have no central
 * counterpart yet — see `MSW-GAPS.md` for the inventory.
 */
// ── SSE event-stream factory (text/event-stream) ────────────────────────
// Used by the realtime SSE client `openRealtimeStream` against `/events`.
// Returns a long-running ReadableStream. By default it emits heartbeats only:
// camera/video simulation must not fabricate AI inference. Explicit fixture
// builds can opt into periodic mocked detection/alarm events derived from the
// static fixtures above. Without this handler the SSE GET falls through to the
// SPA's catch-all `try_files .. /index.html`, the client receives HTML,
// can't parse it, and reconnect-loops forever (the topbar "RECONNECTING"
// indicator stays lit).
// Demo SSE event taxonomy (#21).
// Types here must match the union in `libs/ts/api-client/src/realtime/types.ts`
// which itself mirrors what the central .NET bridges publish
// (services/central/src/Modules/*/Application/Realtime/*Bridge.cs).
// Anything outside that union will be dropped silently by the operator
// console's `useRealtimeStream` dispatcher.
const DEMO_CAMERA_IDS = DEMO_CAMERA_ROSTER.map((camera) => camera.shortId);

// Map each cam id to the site that owns it (mirrors the `cameras` fixture).
const DEMO_CAMERA_SITE: Readonly<Record<string, string>> = Object.fromEntries(
  cameras.map((camera) => [camera.id, camera.siteId]),
);

// Sample plate strings for ALPR demo. Mix of UAE-style plates so the
// operator-console plate-render path sees realistic input.
const DEMO_PLATES = [
  'DXB-A-12345',
  'AUH-Q-78901',
  'SHJ-1-22334',
  'DXB-G-55667',
  'AUH-2-99000',
] as const;

const parseRequestedTopics = (request: Request): ReadonlySet<string> | null => {
  const raw = new URL(request.url).searchParams.get('topics');
  const topics = raw
    ?.split(',')
    .map((topic) => topic.trim())
    .filter((topic) => topic.length > 0);
  return topics && topics.length > 0 ? new Set(topics) : null;
};

const shouldEmitMockRealtimeFixtures = (): boolean => {
  const meta = import.meta as ImportMeta & {
    env?: Record<string, string | undefined>;
  };
  return (
    meta.env?.VITE_DEMO_MSW_REALTIME_FIXTURE === '1' ||
    meta.env?.VITE_DEMO_DETECTION_FIXTURE === '1'
  );
};

const ssEventStream = (requestedTopics: ReadonlySet<string> | null = null) => {
  const encoder = new TextEncoder();
  let cleanup: (() => void) | undefined;
  return new ReadableStream({
    start(controller) {
      controller.enqueue(encoder.encode('retry: 5000\n\n'));
      // Heartbeat comment every 15 s so proxies don't close the connection.
      const heartbeat = setInterval(() => {
        controller.enqueue(encoder.encode(`: heartbeat ${new Date().toISOString()}\n\n`));
      }, 15000);
      if (!shouldEmitMockRealtimeFixtures()) {
        cleanup = () => {
          clearInterval(heartbeat);
        };
        return;
      }

      let i = 0;
      let bboxFrame = 0;

      // Round-robin camera selection so every cam id appears at roughly
      // the same cadence in a 16-cam grid.
      const camAt = (offset: number): string =>
        DEMO_CAMERA_IDS[(offset + i) % DEMO_CAMERA_IDS.length]!;
      // The site map covers every key in DEMO_CAMERA_IDS by construction,
      // but TS sees a wide `Record<string, string>` index — narrow with a
      // string fallback so downstream return types stay non-optional.
      const siteOf = (cameraId: string): string => DEMO_CAMERA_SITE[cameraId] ?? marinaSiteId;

      type Shape = {
        topic: string;
        type: string;
        siteId: string;
        payload: object;
      };
      const acceptsTopic = (topic: string): boolean =>
        requestedTopics === null || requestedTopics.has(topic);
      const enqueueEvent = (evt: Shape & { id: string; timestamp: string }): void => {
        if (!acceptsTopic(evt.topic)) return;
        // Native EventSource dispatches on the `event:` field name; the
        // central side uses the dotted type as-is in the named-event field,
        // not the underscore variant. Keep this matching what the SSE
        // client subscribes to.
        controller.enqueue(
          encoder.encode(`event: ${evt.type}\nid: ${evt.id}\ndata: ${JSON.stringify(evt)}\n\n`),
        );
      };
      // One generator per PSIM event type the operator-console renders.
      // Order is deliberate — the round-robin below cycles through them so
      // each type appears at least once per ~20s (8 types × ~2.5s cadence).
      const eventShapes: ReadonlyArray<() => Shape> = [
        // ── 1. Face-recognition match (AI detection + watchlist hit) ────
        () => {
          const cameraId = camAt(0);
          return {
            topic: 'detections',
            type: 'detection.watchlist_hit',
            siteId: siteOf(cameraId),
            payload: {
              detectionId: `det-${Date.now()}-${i}`,
              watchlistId: 'wl-vip-001',
              watchlistEntryId: `wle-${(i % 12) + 1}`,
              matchType: 'face',
              matchScore: Math.round((0.78 + (i % 18) / 100) * 1000) / 1000,
              cameraId,
            },
          };
        },
        // ── 2. Contact ID panel alarm (Layer 1 hardware alarm) ──────────
        () => {
          const cameraId = camAt(3);
          // Cycle plausible CID + SIA codes so the alarm queue shows variety.
          const cidCodes = ['1130', '1131', '1110', '1120', '1134'] as const;
          const siaCodes = ['BA', 'BV', 'FA', 'HA', 'MA'] as const;
          return {
            topic: 'alarms',
            type: 'alarm.received',
            siteId: siteOf(cameraId),
            payload: {
              alarmId: `alm-${Date.now()}-${i}`,
              accountCode: `1234-${(i % 9) + 1}`,
              siaCode: siaCodes[i % siaCodes.length]!,
              cidEventCode: cidCodes[i % cidCodes.length]!,
              tier: ((i % 3) + 1) as 1 | 2 | 3 | 4 | 5,
              cameraId,
              zone: (i % 32) + 1,
            },
          };
        },
        // ── 3. Operator alert (low-severity incident — tier 4/5) ────────
        () => {
          const cameraId = camAt(5);
          return {
            topic: 'incidents',
            type: 'incident.opened',
            siteId: siteOf(cameraId),
            payload: {
              incidentId: `inc-${Date.now()}-${i}`,
              tier: ((i % 2) + 4) as 4 | 5, // 4 or 5 — operator advisory
              source: 'ai',
              title: `Loitering detected at ${cameraId}`,
              seedAlarmIds: [`alm-${i}-soft-a`],
            },
          };
        },
        // ── 4. Correlation (tier escalation tying multiple alarms) ──────
        () => {
          const cameraId = camAt(7);
          return {
            topic: 'incidents',
            type: 'incident.tier_escalated',
            siteId: siteOf(cameraId),
            payload: {
              incidentId: `inc-corr-${Date.now()}-${i}`,
              fromTier: 3 as 1 | 2 | 3 | 4 | 5,
              toTier: 1 as 1 | 2 | 3 | 4 | 5,
              reason: `correlated ${2 + (i % 3)} alarms within 60s at ${cameraId}`,
            },
          };
        },
        // ── 5. License-plate ALPR match ─────────────────────────────────
        () => {
          const cameraId = camAt(9);
          const plate = DEMO_PLATES[i % DEMO_PLATES.length]!;
          return {
            topic: 'detections',
            type: 'detection.watchlist_hit',
            siteId: siteOf(cameraId),
            payload: {
              detectionId: `det-plate-${Date.now()}-${i}`,
              watchlistId: 'wl-plates-blacklist',
              watchlistEntryId: `wle-plate-${(i % 8) + 1}`,
              matchType: 'plate',
              matchScore: Math.round((0.85 + (i % 12) / 100) * 1000) / 1000,
              cameraId,
              plate,
            },
          };
        },
        // ── 6. Detection verdict (AI confirmed/uncertain — pre-incident) ─
        () => {
          const cameraId = camAt(11);
          const verdicts = ['confirmed', 'uncertain', 'rejected'] as const;
          return {
            topic: 'detections',
            type: 'detection.verdict',
            siteId: siteOf(cameraId),
            payload: {
              detectionId: `det-verdict-${Date.now()}-${i}`,
              cameraId,
              verdict: verdicts[i % verdicts.length]!,
              confidence: Math.round((0.62 + (i % 35) / 100) * 1000) / 1000,
              category: i % 2 === 0 ? 'person' : 'vehicle',
              modelFamily: i % 2 === 0 ? 'yolo11s' : 'edgeface-edge',
              modelVersion: '1.0.0',
              pipelineId: i % 2 === 0 ? 'fixture-t2-object' : 'fixture-t4-face-id',
              inferenceDevice: i % 2 === 0 ? 'GPU' : 'NPU',
              bbox: { x: 420, y: 180, width: 220, height: 360 },
              trackId: i % 3 === 0 ? null : i,
            },
          };
        },
        // ── 7. Camera vitals (health-lite — fleet topology) ─────────────
        () => {
          const cameraId = camAt(13);
          // 1 in 7 we report a degraded camera so the fleet view has
          // colour — the rest are healthy.
          const status: 'online' | 'degraded' | 'offline' = i % 7 === 0 ? 'degraded' : 'online';
          return {
            topic: 'cameras',
            type: 'camera.health',
            siteId: siteOf(cameraId),
            payload: {
              cameraId,
              status,
              latencyMs: 250 + ((i * 37) % 320),
            },
          };
        },
        // ── 8. Incident acknowledged (lifecycle motion in queue) ────────
        () => {
          const cameraId = camAt(15);
          return {
            topic: 'incidents',
            type: 'incident.acknowledged',
            siteId: siteOf(cameraId),
            payload: {
              incidentId: `inc-${Date.now()}-${i}`,
              operatorUserId: `op-demo-${(i % 4) + 1}`,
            },
          };
        },
      ];

      const emitDetectionBbox = (cameraId: string, frame: number, matched: boolean): void => {
        const t = frame / 18;
        const width = matched ? 0.18 : 0.2;
        const height = matched ? 0.32 : 0.22;
        const x = Math.max(0.04, Math.min(0.96 - width, 0.43 + 0.12 * Math.sin(t)));
        const y = Math.max(0.05, Math.min(0.95 - height, 0.32 + 0.08 * Math.cos(t * 1.25)));
        const emittedAtMs = Date.now();
        const detectionId = `det-bbox-${cameraId}-${emittedAtMs}-${frame}`;
        const category = matched ? 'face' : 'person';
        const modelFamily = matched ? 'edgeface' : 'yolo11-s';
        const modelVersion = matched ? '2026.05.face-t4' : '2026.05.object-t2';
        const pipelineId = matched ? 'fixture-t4-face-id' : 'fixture-t2-object';
        const timestamp = new Date(emittedAtMs).toISOString();
        enqueueEvent({
          id: `${emittedAtMs.toString(16)}-bbox-${frame.toString(16)}-${cameraId}`,
          topic: 'detections',
          type: 'detection.bbox',
          timestamp,
          siteId: siteOf(cameraId),
          payload: {
            detectionId,
            cameraId,
            timestampMs: now,
            bbox: {
              x: Math.round(x * 1000) / 1000,
              y: Math.round(y * 1000) / 1000,
              width,
              height,
            },
            confidence: matched ? 0.94 : 0.81,
            matchLabel: matched ? 'DemoSubject-MCP-Step4' : null,
            trackId: matched ? 17 : null,
          },
        });
        enqueueEvent({
          id: `${now.toString(16)}-verdict-${frame.toString(16)}-${cameraId}`,
          topic: 'detections',
          type: 'detection.verdict',
          timestamp,
          siteId: siteOf(cameraId),
          payload: {
            detectionId,
            cameraId,
            verdict: 'confirmed',
            confidence: matched ? 0.94 : 0.81,
            category,
            modelFamily,
            modelVersion,
            pipelineId,
            inferenceDevice: matched ? 'NPU' : 'GPU',
            bbox: { x: 420, y: 180, width: 220, height: 360 },
            trackId: matched ? 17 : null,
          },
        });
      };

      const emitDetectionBboxPair = (): void => {
        bboxFrame += 1;
        emitDetectionBbox('cam-04', bboxFrame, true);
        const rotatingCamera = DEMO_CAMERA_IDS[bboxFrame % DEMO_CAMERA_IDS.length]!;
        if (rotatingCamera !== 'cam-04') {
          emitDetectionBbox(rotatingCamera, bboxFrame, false);
        }
      };

      const tick = () => {
        i += 1;
        const make = eventShapes[i % eventShapes.length]!;
        const env = make();
        enqueueEvent({
          id: `${Date.now().toString(16)}-${i.toString(16)}`,
          topic: env.topic,
          type: env.type,
          timestamp: new Date().toISOString(),
          siteId: env.siteId,
          payload: env.payload,
        });
      };
      // Emit the first event ~250ms after connect so the UI gets visible
      // motion immediately. Then a steady ~2.5s cadence — 8 shapes ×
      // 2.5s = full taxonomy every 20s.
      const kick = setTimeout(() => {
        tick();
        emitDetectionBboxPair();
      }, 250);
      const interval = setInterval(tick, 2500);
      const bboxInterval = setInterval(emitDetectionBboxPair, 750);
      // Hold the stream open until the client disconnects.
      cleanup = () => {
        clearTimeout(kick);
        clearInterval(interval);
        clearInterval(bboxInterval);
        clearInterval(heartbeat);
      };
    },
    cancel(reason) {
      void reason;
      cleanup?.();
      cleanup = undefined;
    },
  });
};

const reviewsTailStream = (): ReadableStream<Uint8Array> => {
  const encoder = new TextEncoder();
  let cleanup: (() => void) | undefined;

  return new ReadableStream({
    start(controller) {
      controller.enqueue(encoder.encode('retry: 5000\n\n'));

      const firstEvent = setTimeout(() => {
        const latest = reviewSegments[0];
        controller.enqueue(
          encoder.encode(`event: review_segment_closed\ndata: ${JSON.stringify(latest)}\n\n`),
        );
      }, 750);
      const heartbeat = setInterval(() => {
        controller.enqueue(encoder.encode(`: heartbeat ${new Date().toISOString()}\n\n`));
      }, 15_000);

      cleanup = () => {
        clearTimeout(firstEvent);
        clearInterval(heartbeat);
      };
    },
    cancel() {
      cleanup?.();
      cleanup = undefined;
    },
  });
};

export const createHandlers = (caller: MockCaller = identityMe): HttpHandler[] => [
  ...fleetSiteHandlers,
  // Realtime SSE — keep open for the client. It emits heartbeat-only by
  // default; fixture events require an explicit VITE_DEMO_* opt-in.
  http.get('/events', ({ request }) => {
    return new HttpResponse(ssEventStream(parseRequestedTopics(request)), {
      status: 200,
      headers: {
        'Content-Type': 'text/event-stream; charset=utf-8',
        'Cache-Control': 'no-cache, no-transform',
        Connection: 'keep-alive',
        'X-Accel-Buffering': 'no',
      },
    });
  }),

  // ── Generated client surface (mirrors openapi.json) ────────────────────
  http.post('/v1/onboarding/registrations', async () => {
    registrationStatus = createRegistrationStatus();
    return HttpResponse.json(
      { status: 'accepted', resumeToken: registrationResumeToken },
      { status: 202 },
    );
  }),
  http.post('/v1/onboarding/registrations/resume', async ({ request }) => {
    if (!hasDemoRegistrationToken(await request.json())) {
      return HttpResponse.json({ error: 'registration unavailable' }, { status: 404 });
    }
    return HttpResponse.json(registrationStatus);
  }),
  http.post('/v1/onboarding/registrations/authority/resolve', async ({ request }) => {
    if (!hasDemoRegistrationToken(await request.json())) {
      return HttpResponse.json({ error: 'registration unavailable' }, { status: 404 });
    }
    registrationStatus = {
      ...registrationStatus,
      status: 'AuthorityResolved',
      organizationName: 'Demo Security Services LLC',
      updatedAtUtc: new Date().toISOString(),
    };
    return HttpResponse.json(registrationStatus);
  }),
  http.post('/v1/onboarding/registrations/contact/challenges', async ({ request }) => {
    if (!hasDemoRegistrationToken(await request.json())) {
      return HttpResponse.json({ error: 'registration unavailable' }, { status: 404 });
    }
    return HttpResponse.json({ status: 'accepted' }, { status: 202 });
  }),
  http.post('/v1/onboarding/registrations/contact/verify', async ({ request }) => {
    const body = await request.json();
    if (!hasDemoRegistrationToken(body) || typeof body !== 'object' || body === null) {
      return HttpResponse.json({ error: 'registration unavailable' }, { status: 404 });
    }
    const emailVerified = registrationStatus.emailVerified || body.channel === 'Email';
    const mobileVerified = registrationStatus.mobileVerified || body.channel === 'Mobile';
    registrationStatus = {
      ...registrationStatus,
      emailVerified,
      mobileVerified,
      status: emailVerified && mobileVerified ? 'ContactVerified' : 'AuthorityResolved',
      updatedAtUtc: new Date().toISOString(),
    };
    return HttpResponse.json(registrationStatus);
  }),
  http.post('/v1/onboarding/registrations/identity', async ({ request }) => {
    if (!hasDemoRegistrationToken(await request.json())) {
      return HttpResponse.json({ error: 'registration unavailable' }, { status: 404 });
    }
    registrationStatus = {
      ...registrationStatus,
      status: 'IdentityVerified',
      updatedAtUtc: new Date().toISOString(),
    };
    return HttpResponse.json(registrationStatus);
  }),
  http.get('/v1/identity/me', async () => {
    await delay(50);
    return HttpResponse.json(identityMe);
  }),
  http.get('/v1/incidents', async ({ request }) => {
    await delay(50);
    const siteId = new URL(request.url).searchParams.get('siteId');
    const rows = siteId ? incidents.filter((incident) => incident.siteId === siteId) : incidents;
    return HttpResponse.json(rows.map(withCorrelationFields));
  }),
  http.get('/v1/incidents/:id', async ({ params }) => {
    const found = incidents.find((i) => i.id === params.id);
    if (!found) return new HttpResponse(null, { status: 404 });
    return HttpResponse.json({
      ...detailFor(found),
      ...withCorrelationFields(found),
    });
  }),
  http.get('/v1/incidents/:id/evidence', async ({ params }) => {
    await delay(80);
    if (params.id === 'inc-1001') return HttpResponse.json(populatedEvidence);
    if (params.id === 'inc-1002') return HttpResponse.json(degradedEvidence);
    if (params.id === 'inc-1003') {
      return HttpResponse.json(
        { detail: 'Incident evidence projection is temporarily unavailable' },
        { status: 503 },
      );
    }
    return HttpResponse.json({
      incidentId: String(params.id),
      generatedAtUtc: iso(0),
      correlationPolicy: 'ExactAlarmId',
      alarmRecordingStatus: 'Unavailable',
      clips: [],
      detections: [],
      sceneDescriptions: [],
    });
  }),
  http.post('/v1/incidents/:id/evidence/clips/:clipId/playback', ({ params }) => {
    const evidence = params.id === 'inc-1001' ? populatedEvidence : degradedEvidence;
    const clip = evidence.clips.find((item) => item.clipId === params.clipId);
    if (!clip || clip.status !== 'available') {
      return HttpResponse.json({ detail: 'Clip is not available for playback' }, { status: 409 });
    }
    return HttpResponse.json({
      url: `/v1/mock-evidence/clips/${clip.clipId}.mp4`,
      expiresAtUtc: iso(5 * 60_000),
    });
  }),
  http.get('/v1/mock-evidence/frames/:frame.svg', ({ params }) => {
    const offset = params.frame === 'lobby-2' ? 36 : 0;
    return new HttpResponse(mockLobbyEvidenceFrame(offset), {
      status: 200,
      headers: { 'Content-Type': 'image/svg+xml' },
    });
  }),
  http.get('/v1/mock-evidence/crops/:crop.jpg', () => {
    const base64 = synthFaceDataUrl.slice(synthFaceDataUrl.indexOf(',') + 1);
    const binary = atob(base64);
    const bytes = Uint8Array.from(binary, (character) => character.charCodeAt(0));
    return new HttpResponse(bytes, {
      status: 200,
      headers: {
        'Content-Type': 'image/jpeg',
        'Content-Length': String(bytes.byteLength),
      },
    });
  }),
  http.get('/v1/mock-evidence/clips/:clipId.mp4', () => {
    const binary = atob(mockIncidentClipMp4Base64);
    const bytes = Uint8Array.from(binary, (character) => character.charCodeAt(0));
    return new HttpResponse(bytes, {
      status: 200,
      headers: {
        'Content-Type': 'video/mp4',
        'Content-Length': String(bytes.byteLength),
        'Cache-Control': 'private, max-age=300',
      },
    });
  }),
  http.post('/v1/ingest/legacy-json-alarm', async ({ request }) => {
    let body: LegacyJsonAlarmRequest = {};
    try {
      body = (await request.json()) as LegacyJsonAlarmRequest;
    } catch {
      body = {};
    }

    const alarmId = crypto.randomUUID();
    const incidentId = crypto.randomUUID() as IncidentId;
    const eventCode =
      typeof body.eventCode === 'string' && body.eventCode.trim().length > 0
        ? body.eventCode
        : 'A15';
    const description =
      parseLegacyAlarmDescription(body.extraJson) ?? `${eventCode} burglary alarm`;

    incidents.unshift({
      id: incidentId,
      tier: 2,
      state: 'open',
      siteId: marinaSiteId as SiteId,
      siteName: 'Marina Tower',
      cameraId: 'cam-01' as CameraId,
      cameraLabel: 'Cam 01 · Lobby Entrance',
      source: 'panel',
      description,
      receivedAt:
        typeof body.occurredAtUtc === 'string' && body.occurredAtUtc.trim().length > 0
          ? body.occurredAtUtc
          : iso(0),
      sspId: 'ssp-acme' as SspId,
      sspName: 'Acme Security',
      ssuId: 'ssu-marina' as SsuId,
      ssuName: 'Marina Holdings',
    });
    relatedAlarmIdsByIncidentId.set(incidentId, [alarmId]);

    return HttpResponse.json({ alarmId }, { status: 202 });
  }),
  http.post('/v1/incidents/:id/detections', async ({ params, request }) => {
    const incidentId = String(params.id);
    if (!incidents.some((incident) => incident.id === incidentId)) {
      return new HttpResponse(null, { status: 404 });
    }
    const body = (await request.json()) as { detectionId?: unknown };
    if (typeof body.detectionId !== 'string') {
      return HttpResponse.json({ detail: 'detectionId is required' }, { status: 400 });
    }
    const related = relatedDetectionIdsByIncidentId.get(incidentId) ?? [];
    if (!related.includes(body.detectionId)) related.push(body.detectionId);
    relatedDetectionIdsByIncidentId.set(incidentId, related);
    return new HttpResponse(null, { status: 204 });
  }),
  http.post('/v1/detections/search', async ({ request }) => {
    const body = (await request.json()) as { cameraId?: unknown; siteId?: unknown };
    const detections = [
      {
        detectionId: '019f0500-4444-7444-8444-444444444444',
        siteId: '44444444-4444-4444-4444-444444444402',
        cameraId: '66666666-6666-6666-6666-666666666601',
        eventClass: 'FaceMatch',
        modelFamily: 'scrfd-2.5g',
        modelVersion: 'opset13',
        inferenceDevice: 'CPU',
        aiConfidence: 0.91,
        occurredAtUtc: new Date().toISOString(),
      },
    ];
    return HttpResponse.json({
      items: detections.filter(
        (detection) =>
          (typeof body.cameraId !== 'string' || detection.cameraId === body.cameraId) &&
          (typeof body.siteId !== 'string' || detection.siteId === body.siteId),
      ),
    });
  }),
  // Real central uses flat verbs (`/{id}/acknowledge`, `/{id}/dispatch`,
  // `/{id}/close`) — see `IncidentEndpoints.cs`. The single regex handler
  // below covers all three so the operator-console mutation hooks resolve
  // in dev exactly the way they will in prod.
  http.post(/\/v1\/incidents\/[^/]+\/(acknowledge|dispatch|close)$/, async ({ request }) => {
    const segments = new URL(request.url).pathname.split('/');
    const id = segments[3];
    const action = segments[4] as 'acknowledge' | 'dispatch' | 'close';
    const found = incidents.find((i) => i.id === id);
    if (!found) return new HttpResponse(null, { status: 404 });
    const body = (await request.json().catch(() => ({}))) as {
      dispatchedTo?: string;
      closureCode?: string;
    };
    const step = {
      acknowledge: { state: 'acknowledged' as const, description: 'Operator acknowledged the alarm' },
      dispatch: {
        state: 'dispatched' as const,
        description: body.dispatchedTo
          ? `Guard dispatched: ${body.dispatchedTo}`
          : 'Guard dispatched',
      },
      close: {
        state: 'closed' as const,
        description: body.closureCode
          ? `Incident closed: ${body.closureCode}`
          : 'Incident closed',
      },
    }[action];
    found.state = step.state;
    const entries = incidentActionTimeline.get(found.id) ?? [];
    entries.push({
      id: `${found.id}-${action}-${entries.length + 1}`,
      kind: step.state,
      at: new Date().toISOString(),
      actor: 'NOC desk',
      description: step.description,
    });
    incidentActionTimeline.set(found.id, entries);
    return HttpResponse.json(detailFor(found));
  }),
  // PagedResultOfCameraResponse, filtered by site and recorder like central.
  http.get('*/v1/cameras', async ({ request }) => {
    const url = new URL(request.url);
    const siteId = url.searchParams.get('siteId');
    const nvrId = url.searchParams.get('nvrId');
    const page = Math.max(1, Number(url.searchParams.get('page') ?? 1) || 1);
    const pageSize = Math.max(1, Number(url.searchParams.get('pageSize') ?? 50) || 50);
    const filtered = cameras.filter(
      (camera) =>
        (!siteId || camera.siteId === siteId) &&
        (!nvrId || camera.nvrId === nvrId),
    );
    return HttpResponse.json({
      items: filtered.slice((page - 1) * pageSize, page * pageSize).map(cameraResponse),
      total: filtered.length,
      page,
      pageSize,
      totalPages: Math.max(1, Math.ceil(filtered.length / pageSize)),
    });
  }),
  http.get('*/v1/cameras/:id', async ({ params }) => {
    const found = mockCameraById(params.id);
    return found ? HttpResponse.json(cameraResponse(found)) : new HttpResponse(null, { status: 404 });
  }),
  http.post('/v1/cameras/onboarding/probe', async ({ request }) => {
    const body = (await request.json()) as { address?: unknown };
    await delay(350);
    return HttpResponse.json({
      sessionId: 'mock-nvr-session',
      method: 'onvif',
      device: {
        vendor: 'Hikvision',
        model: 'DS-7732NI-I4',
        address: typeof body.address === 'string' ? body.address : '10.24.8.20',
      },
      cameras: nvrProbeCandidates,
      expiresAt: new Date(Date.now() + 10 * 60_000).toISOString(),
    });
  }),
  http.post('/v1/cameras/onboarding/import', async ({ request }) => {
    const body = (await request.json()) as {
      siteId?: string;
      cameraKeys?: string[];
      admissionSource?: { admissionId: string };
    };
    const selected = nvrProbeCandidates.filter((candidate) =>
      body.cameraKeys?.includes(candidate.key),
    );
    const imported = selected.map((candidate) => {
      const existing = cameras.find((camera) => camera.name === candidate.name);
      if (existing) return existing;
      const camera: VideoConsoleMockCamera = {
        id: `imported-${candidate.channelNumber}` as CameraId,
        nvrId: body.admissionSource?.admissionId ?? siteNvrId(body.siteId ?? marinaSiteId),
        siteId: (body.siteId ?? marinaSiteId) as SiteId,
        name: candidate.name,
        status: 'connecting',
        playbackConfigured: true,
        tier: 'TierC',
        vendor: 'Hikvision',
        model: candidate.name,
      };
      cameras.push(camera);
      return camera;
    });
    return HttpResponse.json({ cameras: imported.map(cameraResponse) });
  }),
  http.post('/v1/cameras/', async ({ request }) => {
    const body = (await request.json()) as Record<string, unknown>;
    const id = `manual-${cameras.length + 1}` as CameraId;
    const name = typeof body.model === 'string' ? body.model : 'Manual camera';
    const camera: VideoConsoleMockCamera = {
      id,
      nvrId: typeof body.nvrId === 'string' ? body.nvrId : null,
      siteId: (typeof body.siteId === 'string' ? body.siteId : marinaSiteId) as SiteId,
      name,
      status: 'connecting',
      playbackConfigured:
        typeof body.playbackUrlTemplate === 'string' && body.playbackUrlTemplate.length > 0,
      tier: 'TierC',
      vendor: 'Generic',
      model: name,
    };
    cameras.push(camera);
    return HttpResponse.json(cameraResponse(camera), { status: 201 });
  }),
  http.post('/v1/cameras/:id/enable', ({ params }) =>
    HttpResponse.json({ cameraId: params.id }),
  ),
  http.post('/v1/cameras/:id/tier', async ({ params, request }) => {
    const found = cameras.find((camera) => camera.id === params.id);
    if (!found) return new HttpResponse(null, { status: 404 });
    const body = (await request.json()) as { tier?: unknown };
    if (typeof body.tier !== 'string') {
      return HttpResponse.json({ detail: 'tier is required' }, { status: 400 });
    }
    found.tier = body.tier;
    return HttpResponse.json({ tier: found.tier });
  }),
  http.get('/v1/cameras/:id/recordings', ({ params, request }) => {
    const found = cameras.find((camera) => camera.id === params.id);
    if (!found) return new HttpResponse(null, { status: 404 });
    const query = new URL(request.url).searchParams;
    return HttpResponse.json({ supported: found.playbackConfigured, segments: found.playbackConfigured ? [{ from: query.get('from'), to: query.get('to') }] : [], gaps: [] });
  }),
  http.post('/v1/cameras/playback/stop', () => new HttpResponse(null, { status: 204 })),
  http.post('/v1/cameras/:id/playback', async ({ params }) => {
    const found = cameras.find((camera) => camera.id === params.id);
    if (!found) return new HttpResponse(null, { status: 404 });
    if (!found.playbackConfigured) {
      return HttpResponse.json({ detail: 'Playback is not configured' }, { status: 422 });
    }
    return HttpResponse.json({
      url: `/v1/mock-evidence/clips/video-console-${String(params.id)}.mp4`,
      expiresAt: new Date(Date.now() + 10 * 60_000).toISOString(),
      format: 'fragmented-mp4',
    });
  }),

  // ──────────────────────────────────────────────────────────────────
  // W8A — Camera Evidence mocks (admin-console /cameras/{id} surface).
  // These three handlers exist for design-time dev only. The W8.0 no-mock
  // acceptance harness fails loud if MSW serves these against real central.
  // ──────────────────────────────────────────────────────────────────

  // Snapshot: smallest valid JPEG payload so the dev preview has bytes to
  // render without requiring an edge appliance. Real central proxies go2rtc.
  http.get('/v1/cameras/:id/snapshot', async () => {
    const tinyJpeg = new Uint8Array([
      0xff, 0xd8, 0xff, 0xe0, 0x00, 0x10, 0x4a, 0x46, 0x49, 0x46, 0x00, 0x01, 0x01, 0x00, 0x00,
      0x01, 0x00, 0x01, 0x00, 0x00, 0xff, 0xdb, 0x00, 0x43, 0x00, 0x08, 0x06, 0x06, 0x07, 0x06,
      0x05, 0x08, 0x07, 0x07, 0x07, 0x09, 0x09, 0x08, 0x0a, 0x0c, 0x14, 0x0d, 0x0c, 0x0b, 0x0b,
      0x0c, 0x19, 0x12, 0x13, 0x0f, 0x14, 0x1d, 0x1a, 0x1f, 0x1e, 0x1d, 0x1a, 0x1c, 0x1c, 0x20,
      0x24, 0x2e, 0x27, 0x20, 0x22, 0x2c, 0x23, 0x1c, 0x1c, 0x28, 0x37, 0x29, 0x2c, 0x30, 0x31,
      0x34, 0x34, 0x34, 0x1f, 0x27, 0x39, 0x3d, 0x38, 0x32, 0x3c, 0x2e, 0x33, 0x34, 0x32, 0xff,
      0xc0, 0x00, 0x0b, 0x08, 0x00, 0x01, 0x00, 0x01, 0x01, 0x01, 0x11, 0x00, 0xff, 0xc4, 0x00,
      0x14, 0x00, 0x01, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00,
      0x00, 0x00, 0x00, 0x00, 0xff, 0xc4, 0x00, 0x14, 0x10, 0x01, 0x00, 0x00, 0x00, 0x00, 0x00,
      0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0xff, 0xda, 0x00, 0x08,
      0x01, 0x01, 0x00, 0x00, 0x3f, 0x00, 0x37, 0xff, 0xd9,
    ]);
    return new HttpResponse(tinyJpeg, {
      status: 200,
      headers: { 'Content-Type': 'image/jpeg' },
    });
  }),

  http.get('/v1/cameras/:id/stream-token', async ({ params }) => {
    // Per-camera HLS URLs (#21). Cycle the 8 sims/rtsp-cameras source clips
    // 2× across the 16 demo cameras: cam-01 + cam-09 → sim cam-01 (crowd,
    // face-rich), cam-02 + cam-10 → cam-02 (retail), …, cam-08 + cam-16 →
    // cam-08 (parking night). Source-of-truth for clip mapping is the
    // mediamtx `paths:` block in `sims/rtsp-cameras/mediamtx.yml`.
    //
    // MediaMTX serves LL-HLS on port 8888 at `/<path>/index.m3u8` (see
    // docker-compose.yml). If the operator-console is not running on the
    // sims host, set VITE_SIM_HLS_BASE to the right origin; if you don't
    // want the sims stack at all, set VITE_DEMO_HLS_URL to a single fixed
    // playlist (the legacy "every tile identical" demo mode).
    const cameraId = params.id as string;
    if (import.meta.env.MODE === 'demo' || import.meta.env.VITE_VIDEO_CONSOLE_FIXTURE === '1') {
      return HttpResponse.json({
        url: `/v1/mock-evidence/clips/video-console-${cameraId}.mp4`,
        expiresAt: new Date(Date.now() + 60_000).toISOString(),
        format: 'll-hls',
        token: 'msw-stub-token.payload.signature',
        cameraId,
      });
    }
    const sourceCameraId = showcaseSourceCameraId(cameraId) ?? cameraId;
    const explicitFallback = import.meta.env.VITE_DEMO_HLS_URL;
    const simBase = import.meta.env.VITE_SIM_HLS_BASE ?? 'http://localhost:8888';

    // Deterministic mod-8 mapping: extract the numeric suffix from a
    // "cam-NN" id; for unknown shapes fall back to the FNV-1a 32-bit hash
    // of the id mod 8 so even bespoke ids land on a real sim slot.
    const numericMatch = /^cam-(\d+)$/.exec(sourceCameraId);
    let slot: number;
    if (numericMatch) {
      slot = ((parseInt(numericMatch[1]!, 10) - 1) % 8) + 1;
    } else {
      // FNV-1a 32-bit so the mapping is stable + tiny.
      let h = 0x811c9dc5;
      for (let i = 0; i < sourceCameraId.length; i++) {
        h ^= sourceCameraId.charCodeAt(i);
        h = Math.imul(h, 0x01000193) >>> 0;
      }
      slot = (h % 8) + 1;
    }
    const simSlot = `cam-${String(slot).padStart(2, '0')}`;
    const url = explicitFallback ?? `${simBase}/${simSlot}/index.m3u8`;
    return HttpResponse.json({
      url,
      expiresAt: new Date(Date.now() + 60_000).toISOString(),
      format: 'll-hls',
      token: 'msw-stub-token.payload.signature',
      cameraId,
    });
  }),

  // Health-lite SSE stub — a finite stream of canned snapshots so the auditor
  // sees something move during dev. Real central forwards live MQTT events.
  http.get('/v1/cameras/:id/health', async ({ params }) => {
    const id = params.id as string;
    const stream = new ReadableStream({
      async start(controller) {
        const encoder = new TextEncoder();
        const push = (data: object): void => {
          const frame = `event: health-lite\ndata: ${JSON.stringify(data)}\n\n`;
          controller.enqueue(encoder.encode(frame));
        };
        controller.enqueue(encoder.encode('retry: 5000\n\n'));
        for (let i = 0; i < 5; i++) {
          push({
            cameraId: id,
            observedAt: new Date().toISOString(),
            publishedAt: new Date().toISOString(),
            brightnessMean: 110 + Math.random() * 20,
            contrast: 38 + Math.random() * 8,
            edgeDensity: 0.18,
            samplePeriodMs: 1000,
            frameCount: 25 * (i + 1),
            blackFrame: false,
            whiteFrame: false,
            tamperingActive: false,
            frozenFrame: false,
            sourceStalled: false,
          });
          await delay(1000);
        }
        controller.close();
      },
    });
    return new HttpResponse(stream, {
      status: 200,
      headers: {
        'Content-Type': 'text/event-stream',
        'Cache-Control': 'no-cache',
      },
    });
  }),

  // ─────────────────────────────────────────────────────────────────────
  // W8B — Demo-Gate Replay (admin-console /audit/incidents/{id}/replay).
  // Design-time only: real central serves DemoGateEvidence from the replay
  // endpoint. The no-mock harness asserts these handlers are NOT active when
  // running against the real demo gate.
  // ─────────────────────────────────────────────────────────────────────
  http.get('/v1/audit/incidents/:incidentId/replay', async ({ params }) => {
    const incidentId = params.incidentId as string;
    const incidentAt = '2026-05-03T12:00:00Z';
    const cameraId = '11111111-2222-3333-4444-555555555555';
    const baseFrameAt = new Date(incidentAt).getTime();
    const frames = Array.from({ length: 5 }, (_, i) => ({
      detectionId: `det-${i.toString().padStart(2, '0')}-${incidentId.slice(0, 8)}`,
      frameAt: new Date(baseFrameAt + i * 600).toISOString(),
      modelId: 'scrfd-2.5gf',
      modelVersion: '2024.06',
      device: 'iGPU',
      score: 0.142 + i * 0.027, // raw cosine distance per W8.0 contract
      signedFrameUrl: `https://seaweed.cguard.local/frames/${incidentId}/${i}.jpg?X-Amz-Signature=mock`,
      frameWidth: 1920,
      frameHeight: 1080,
    }));
    return HttpResponse.json({
      incidentId,
      cameraId,
      cameraName: 'Lobby Cam (mock)',
      incidentAt,
      frames,
    });
  }),

  http.post('/v1/admin/sira-bundles/:bundleId/audit-trail', async ({ params, request }) => {
    const bundleId = params.bundleId as string;
    const body = (await request.json()) as {
      incidentId: string;
      replaySnapshotAtUtc: string;
      frameCount: number;
    };
    void body;
    return HttpResponse.json({ bundleId, approvalState: 'AuditPending' });
  }),

  http.get('/v1/sites', async ({ request }) => {
    const url = new URL(request.url);
    const owningSsuId = url.searchParams.get('owningSsuId');
    const page = Math.max(1, Number(url.searchParams.get('page') ?? '1') || 1);
    const pageSize = Math.max(1, Number(url.searchParams.get('pageSize') ?? '50') || 50);
    const filtered = sites.filter(
      (site) => !owningSsuId || site.owningSsuId === centralSsuId(owningSsuId as SsuId),
    );
    return HttpResponse.json({
      items: filtered.slice((page - 1) * pageSize, page * pageSize),
      page,
      pageSize,
      total: filtered.length,
      totalPages: Math.max(1, Math.ceil(filtered.length / pageSize)),
    });
  }),
  http.post('/v1/sites', async ({ request }) => {
    const body = (await request.json()) as Partial<SiteResponse>;
    const ssu = ssus.find((item) => centralSsuId(item.id) === body.owningSsuId);
    if (!body.name?.trim() || !ssu) {
      return HttpResponse.json(
        { title: 'Name and an existing SSU are required.', status: 400 },
        { status: 400 },
      );
    }
    const site = siteFixture({
      siteId: crypto.randomUUID(),
      owningSsuId: centralSsuId(ssu.id),
      owningSspId: centralSspId(ssu.sspId),
      name: body.name.trim(),
      city: body.city ?? 'Dubai',
      emirate: body.emirate ?? 'Dubai',
      status: 'Provisioning',
      addressLine1: body.addressLine1 ?? body.name.trim(),
      addressLine2: body.addressLine2 ?? null,
      ...(body.latitude === undefined ? {} : { latitude: body.latitude }),
      ...(body.longitude === undefined ? {} : { longitude: body.longitude }),
      ...(body.tier ? { tier: body.tier } : {}),
      ...(body.billingTier ? { billingTier: body.billingTier } : {}),
      createdAtUtc: iso(0),
    });
    sites.push(site);
    return HttpResponse.json(site, { status: 201 });
  }),
  http.get('/v1/sites/:id', async ({ params }) => {
    const found = sites.find((s) => s.siteId === params.id);
    return found ? HttpResponse.json(found) : new HttpResponse(null, { status: 404 });
  }),
  http.get('/v1/health/system', async () => HttpResponse.json(health)),

  // Reviews is hand-written in api-client (not in the generated OpenAPI tree
  // yet), so it needs an explicit demo fixture alongside the generated mocks.
  http.get('*/v1/reviews', ({ request }) => {
    const query = new URL(request.url).searchParams;
    const cameraId = query.get('camera');
    const severity = query.get('severity');
    const since = query.get('since');
    const before = query.get('before');
    const openOnly = query.get('open_only') === 'true';
    const startingAfter = query.get('starting_after');
    const requestedLimit = Number.parseInt(query.get('limit') ?? '50', 10);
    const limit = Number.isNaN(requestedLimit) ? 50 : Math.min(Math.max(requestedLimit, 1), 200);

    const filtered = reviewSegments.filter((segment) => {
      if (cameraId && segment.cameraId !== cameraId) return false;
      if (severity && segment.severity !== severity) return false;
      if (since && segment.startTime < since) return false;
      if (before && segment.startTime >= before) return false;
      if (openOnly && segment.closedAt !== null) return false;
      return true;
    });
    const cursorIndex = startingAfter
      ? filtered.findIndex((segment) => segment.segmentId === startingAfter)
      : -1;
    const pageStart = cursorIndex >= 0 ? cursorIndex + 1 : 0;
    const items = filtered.slice(pageStart, pageStart + limit);
    const hasNextPage = pageStart + items.length < filtered.length;

    return HttpResponse.json({
      items,
      nextCursor: hasNextPage ? (items.at(-1)?.segmentId ?? null) : null,
    });
  }),

  http.get(/\/v1\/reviews:tail$/, () => {
    return new HttpResponse(reviewsTailStream(), {
      status: 200,
      headers: {
        'Content-Type': 'text/event-stream; charset=utf-8',
        'Cache-Control': 'no-cache, no-transform',
        Connection: 'keep-alive',
        'X-Accel-Buffering': 'no',
      },
    });
  }),

  http.get('*/v1/reviews/:segmentId/playback', ({ params }) => {
    const found = reviewSegments.some((segment) => segment.segmentId === params.segmentId);
    if (!found) return new HttpResponse(null, { status: 404 });

    return HttpResponse.json(
      {
        title: 'Not Found',
        detail: 'reviews.playback.clip_not_yet_linked',
      },
      { status: 404 },
    );
  }),

  http.get('*/v1/reviews/:segmentId', ({ params }) => {
    const found = reviewSegments.find((segment) => segment.segmentId === params.segmentId);
    return found ? HttpResponse.json(found) : new HttpResponse(null, { status: 404 });
  }),

  http.post('/v1/reviews/search', async ({ request }) => {
    const tenantHeader = request.headers.get('X-CGuard-Tenant');
    if (!tenantHeader) {
      return HttpResponse.json(
        {
          title: 'Bad Request',
          detail: 'X-CGuard-Tenant header is required for the SemanticSearch surface.',
        },
        { status: 400 },
      );
    }
    if (!isGuid(tenantHeader)) {
      return HttpResponse.json(
        {
          title: 'Bad Request',
          detail: 'X-CGuard-Tenant must be a Guid string for the SemanticSearch surface.',
        },
        { status: 400 },
      );
    }

    const body = (await request.json()) as SemanticSearchBody;
    if (!bodyQuery(body)) {
      return HttpResponse.json(
        {
          title: 'Bad Request',
          detail: 'SemanticSearch query must not be empty.',
        },
        { status: 400 },
      );
    }

    const results = semanticReviewResults
      .filter((item) => matchesSemanticFilter(item, body, item.text))
      .slice(0, searchLimit(body.limit))
      .map(({ text: _text, ...item }) => item);

    return HttpResponse.json({
      results,
      modelVersion: 'jina-clip-v1@2024-06',
    });
  }),

  http.post('/v1/alarm-scene-descriptions/search', async ({ request }) => {
    const tenantHeader = request.headers.get('X-CGuard-Tenant');
    if (!tenantHeader) {
      return HttpResponse.json(
        {
          title: 'Bad Request',
          detail: 'X-CGuard-Tenant header is required for the SemanticSearch surface.',
        },
        { status: 400 },
      );
    }
    if (!isGuid(tenantHeader)) {
      return HttpResponse.json(
        {
          title: 'Bad Request',
          detail: 'X-CGuard-Tenant must be a Guid string for the SemanticSearch surface.',
        },
        { status: 400 },
      );
    }

    const body = (await request.json()) as SemanticSearchBody;
    if (!bodyQuery(body)) {
      return HttpResponse.json(
        {
          title: 'Bad Request',
          detail: 'SemanticSearch query must not be empty.',
        },
        { status: 400 },
      );
    }

    const results = semanticSceneResults
      .filter((item) =>
        matchesSemanticFilter(
          item,
          body,
          `${item.sceneCaption} ${item.sceneDescription} ${item.stage ?? ''}`,
        ),
      )
      .slice(0, searchLimit(body.limit));

    return HttpResponse.json({
      results,
      modelVersion: 'jina-clip-v1@2024-06',
      indexedSceneCount: semanticSceneResults.filter((item) => {
        if (item.tenantId !== tenantHeader) return false;
        if (body.siteId && item.siteId !== body.siteId) return false;
        if (body.cameraId && item.cameraId !== body.cameraId) return false;
        return true;
      }).length,
    });
  }),

  // ---- SSP Portal endpoints ----
  http.get('/api/ssp/portfolio', async () => {
    const ssp = ssps[0]!;
    return HttpResponse.json({
      ssp,
      kpis: {
        ssuCount: ssp.ssuCount,
        siteCount: ssp.siteCount,
        sitesLive: ssp.edgeOnline,
        sitesMaint: 1,
        sitesDown: ssp.edgeTotal - ssp.edgeOnline,
        slaAttainment30d: ssp.slaAttainment30d,
        billingCycleStatus: 'open',
        billingCycleProgressPct: 76,
        incidents24h: 47,
      },
    });
  }),
  http.get('/v1/tenants/ssus/', async ({ request }) => {
    const url = new URL(request.url);
    const sspId = url.searchParams.get('owningSspId');
    const items = sspId ? ssus.filter((ssu) => ssu.sspId === fixtureSspId(sspId)) : ssus;
    return HttpResponse.json({
      items: items.map(toCentralSsu),
      page: 1,
      pageSize: items.length,
      total: items.length,
    });
  }),
  http.post('/v1/tenants/ssus/', async ({ request }) => {
    const body = (await request.json()) as Partial<{
      owningSspId: string;
      legalName: string;
      tradeName: string;
      contactEmail: string;
      billingAddress: string;
      contractStartUtc: string;
    }>;
    const created: SsuSummary = {
      id: crypto.randomUUID() as SsuId,
      name: body.tradeName ?? 'New SSU',
      legalName: body.legalName ?? 'New SSU LLC',
      sspId: fixtureSspId(body.owningSspId ?? CENTRAL_SSP_IDS['ssp-acme']),
      sspName: 'Acme Security',
      status: 'active',
      siteCount: 0,
      contactEmail: body.contactEmail ?? 'tbd@example.ae',
      billingAddress: body.billingAddress ?? 'Dubai, United Arab Emirates',
      contractStartUtc: body.contractStartUtc ?? iso(0),
      billingPlan: '',
      createdAt: iso(0),
    };
    ssus.push(created);
    return HttpResponse.json(toCentralSsu(created), { status: 201 });
  }),
  // BillingPlan catalogue served by the Tenants module — mirrors the central
  // service's GET /v1/tenants/billing-plans endpoint (see
  // services/central/src/Modules/Tenants/Presentation/Endpoints/BillingPlansEndpoint.cs).
  http.get('/v1/tenants/billing-plans', async () =>
    HttpResponse.json({
      items: [
        { id: 'Standard', label: 'Standard — pay-per-site monthly' },
        { id: 'Volume', label: 'Volume — discounted (≥100 sites)' },
        { id: 'Government', label: 'Government / SIRA-internal' },
        { id: 'Enterprise', label: 'Enterprise — per-SSP fixed-fee' },
      ],
    }),
  ),
  http.get('/v1/tenants/ssus/:ssuId', async ({ params }) => {
    const ssu = ssus.find((item) => centralSsuId(item.id) === params.ssuId);
    return ssu ? HttpResponse.json(toCentralSsu(ssu)) : new HttpResponse(null, { status: 404 });
  }),
  http.get('/api/ssp/billing/current', async () =>
    HttpResponse.json(invoices.find((i) => i.status === 'draft')),
  ),
  http.get('/api/ssp/billing/invoices', async () => HttpResponse.json(invoices)),
  http.get('/api/ssp/reports/catalog', async () =>
    HttpResponse.json([
      {
        id: 'sla-monthly',
        name: 'SLA attainment · monthly',
        cadence: 'monthly',
      },
      {
        id: 'incidents-by-tier',
        name: 'Incidents by tier · 30d',
        cadence: 'on-demand',
      },
      {
        id: 'edge-fleet-health',
        name: 'Edge fleet health · weekly',
        cadence: 'weekly',
      },
      {
        id: 'billing-reconciliation',
        name: 'Billing reconciliation',
        cadence: 'monthly',
      },
    ]),
  ),
  // GET/POST /v1/report-runs/ defined once below in the unified Reporting block.
  // Caller visibility is applied before explicit SSP/SSU filters and pagination.
  http.get('/v1/identity/users/', async ({ request }) => {
    const url = new URL(request.url);
    const sspId = url.searchParams.get('sspId');
    const ssuId = url.searchParams.get('ssuId');
    const page = Math.max(1, Number(url.searchParams.get('page') ?? 1) || 1);
    const pageSize = Math.min(200, Math.max(1, Number(url.searchParams.get('pageSize') ?? 50) || 1));
    const filtered = platformUsers.filter((user) =>
      canReadUser(caller, user) &&
      (!sspId || user.sspId === sspId) &&
      (!ssuId || user.ssuId === ssuId),
    );
    const start = (page - 1) * pageSize;
    return HttpResponse.json({
      items: filtered.slice(start, start + pageSize),
      page,
      pageSize,
      total: filtered.length,
      totalPages: Math.ceil(filtered.length / pageSize),
    });
  }),

  // ---- Admin Console endpoints ----
  // AdminOverviewResponse: nested counters, with the fleet taken from the shared appliance set.
  http.get('/v1/admin/overview', async () =>
    HttpResponse.json({
      ssps: {
        total: ssps.length,
        active: ssps.filter((s) => s.status === 'active').length,
      },
      sites: {
        total: sites.length,
        live: sites.filter((site) => site.status === 'Live').length,
        offline: sites.filter((site) => site.status !== 'Live').length,
      },
      cameras: {
        total: cameras.length,
        active: cameras.filter((camera) => camera.status === 'live').length,
      },
      incidents24h: {
        total: incidents.length,
        tier1: incidents.filter((incident) => incident.tier === 1).length,
        tier2: incidents.filter((incident) => incident.tier === 2).length,
        tier3: incidents.filter((incident) => incident.tier === 3).length,
        tier4: incidents.filter((incident) => incident.tier === 4).length,
        tier5: incidents.filter((incident) => incident.tier === 5).length,
      },
      ai: {
        modelsActive: 3,
        inferencesLast24h: 18_420,
        faceMatchesLast24h: 27,
        accuracy30d: 0.917,
        accuracyTrend: [0.901, 0.905, 0.911, 0.913, 0.917, 0.919, 0.917],
        confirmationRate30d: 0.871,
      },
      fleet: fleetOverviewCounters(),
      sla: { metTodayPercent: 98.4, targetPercent: 99.5, alarmsInBreachCount: 3 },
      generatedAtUtc: iso(0),
    }),
  ),
  // No trailing slash: MSW then matches both /v1/tenants/ssps and /v1/tenants/ssps/.
  http.get('/v1/tenants/ssps', async () =>
    HttpResponse.json({
      items: ssps.map(toCentralSsp),
      page: 1,
      pageSize: ssps.length,
      total: ssps.length,
    }),
  ),
  http.post('/v1/tenants/ssps', async ({ request }) => {
    const body = (await request.json()) as Partial<{
      name: string;
      contactEmail: string;
      contractStartUtc: string;
      billingPlan: string;
    }>;
    const created: SspSummary = {
      id: crypto.randomUUID() as SspId,
      name: body.name ?? 'New SSP',
      status: 'active',
      billingPlan: body.billingPlan ?? 'Standard',
      contractStartUtc: body.contractStartUtc ?? iso(0),
      ssuCount: 0,
      siteCount: 0,
      edgeOnline: 0,
      edgeTotal: 0,
      slaAttainment30d: 0,
      contactEmail: body.contactEmail ?? 'tbd@example.ae',
      emirates: [],
      createdAt: iso(0),
    };
    ssps.push(created);
    return HttpResponse.json(toCentralSsp(created), { status: 201 });
  }),
  http.post('/v1/tenants/ssps/:id/:action', async ({ params }) => {
    const ssp = ssps.find((item) => centralSspId(item.id) === params.id);
    if (!ssp) return new HttpResponse(null, { status: 404 });
    if (params.action === 'suspend') ssp.status = 'suspended';
    if (params.action === 'decommission') ssp.status = 'decommissioned';
    if (params.action === 'reactivate') ssp.status = 'active';
    return new HttpResponse(null, { status: 204 });
  }),
  // GET /v1/tenants/ssus/ unified above (handles both SSP-filter and admin
  // all-list cases).
  http.post('/v1/tenants/ssus/:id/transfer', async ({ params, request }) => {
    const body = await readMockJsonRecord(request);
    if (
      body === null ||
      !isNonEmptyMockString(body.newOwningSspId) ||
      !isNonEmptyMockString(body.reason)
    ) {
      return HttpResponse.json(
        { detail: 'newOwningSspId and a non-empty reason are required' },
        { status: 400 },
      );
    }
    const ssu = ssus.find((item) => centralSsuId(item.id) === params.id);
    if (!ssu) return new HttpResponse(null, { status: 404 });
    ssu.sspId = fixtureSspId(body.newOwningSspId);
    const target = ssps.find((item) => item.id === ssu.sspId);
    if (target) ssu.sspName = target.name;
    return new HttpResponse(null, { status: 204 });
  }),
  http.get('/v1/admin/fleet/bundles', async () => HttpResponse.json(otaBundles)),
  http.get('*/v1/admin/edge/workload-releases', async () => HttpResponse.json(workloadReleases)),
  http.get('*/v1/admin/provisioning/releases', async ({ request }) => {
    const url = new URL(request.url);
    const page = Math.max(1, Number(url.searchParams.get('page') ?? '1') || 1);
    const pageSize = Math.max(1, Number(url.searchParams.get('pageSize') ?? '50') || 50);
    const body: Omit<PagedResultOfProvisioningReleaseResponse, 'items'> & {
      items: MockProvisioningRelease[];
    } = {
      items: provisioningReleases.slice((page - 1) * pageSize, page * pageSize),
      page,
      pageSize,
      total: provisioningReleases.length,
      totalPages: Math.max(1, Math.ceil(provisioningReleases.length / pageSize)),
    };
    return HttpResponse.json(body);
  }),
  http.post('/v1/admin/fleet/bundles', async ({ request }) => {
    const body = (await request.json()) as Partial<OtaBundle>;
    const bundle: OtaBundle = {
      id: `ota-${body.family ?? 'edge'}-${body.version ?? Date.now()}`,
      version: body.version ?? '0.0.0',
      family: body.family ?? 'edge',
      publishedAt: iso(0),
      publishedBy: 'platform-admin@uxe.ai',
      cohort: body.cohort ?? 'canary',
      cosignVerified: true,
      rolloutPct: 0,
      autoPaused: false,
    };
    otaBundles.unshift(bundle);
    return HttpResponse.json(bundle, { status: 201 });
  }),
  http.post('/v1/admin/fleet/bundles/:id/pause', async ({ params }) => {
    const bundle = otaBundles.find((b) => b.id === params.id);
    if (!bundle) return new HttpResponse(null, { status: 404 });
    bundle.autoPaused = true;
    return HttpResponse.json(bundle);
  }),
  http.get('/v1/admin/sops', async () => HttpResponse.json(sops)),
  http.get('/v1/admin/sops/:id', async ({ params }) => {
    const sop = sops.find((s) => s.id === params.id);
    if (!sop) return new HttpResponse(null, { status: 404 });
    return HttpResponse.json(sop);
  }),
  http.put('/v1/admin/sops/:id', async ({ params, request }) => {
    const body = (await request.json()) as Partial<SopDocument>;
    const sop = sops.find((s) => s.id === params.id);
    if (!sop) return new HttpResponse(null, { status: 404 });
    // Push the previous version into history before bumping.
    const snapshot: SopVersion = {
      version: sop.version,
      body: sop.body,
      updatedAt: sop.updatedAt,
      updatedBy: sop.updatedBy,
      ...(sop.signedBy ? { signedBy: sop.signedBy } : {}),
      ...(sop.approvedAt ? { approvedAt: sop.approvedAt } : {}),
    };
    sop.history = [...(sop.history ?? []), snapshot];
    sop.body = body.body ?? sop.body;
    sop.version += 1;
    sop.updatedAt = iso(0);
    sop.updatedBy = 'platform-admin@uxe.ai';
    delete sop.signedBy;
    delete sop.approvedAt;
    return HttpResponse.json(sop);
  }),
  http.post('/v1/admin/sops/:id/approve', async ({ params }) => {
    const sop = sops.find((s) => s.id === params.id);
    if (!sop) return new HttpResponse(null, { status: 404 });
    sop.signedBy = 'platform-admin@uxe.ai';
    sop.approvedAt = iso(0);
    return HttpResponse.json(sop);
  }),
  // Phase B B5: Watchlists module — mounted on the real central paths so the
  // admin-console dev loop exercises the wire contract central serves.
  // Honours the dev-elevated header for the face enrolment endpoint and the
  // RFC-7807 ProblemDetails shape on validation failures.
  http.get('/v1/watchlists/', async ({ request }) => {
    const url = new URL(request.url);
    const page = Math.max(1, Number(url.searchParams.get('page') ?? 1));
    const pageSize = Math.min(200, Math.max(1, Number(url.searchParams.get('pageSize') ?? 50)));
    const slice = watchlistsCatalog.slice((page - 1) * pageSize, page * pageSize);
    return HttpResponse.json({
      items: slice,
      page,
      pageSize,
      total: watchlistsCatalog.length,
    });
  }),
  http.get('/v1/watchlists/entries', async ({ request }) =>
    HttpResponse.json(watchlistEntryPage(new URL(request.url))),
  ),
  http.get('/v1/watchlists/:id/entries', async ({ request, params }) =>
    HttpResponse.json(watchlistEntryPage(new URL(request.url), String(params.id))),
  ),
  http.post('/v1/watchlists/:id/entries/face', async ({ request, params }) => {
    if (request.headers.get('x-cguard-dev-elevated') !== 'true') {
      return HttpResponse.json(
        {
          type: 'about:blank',
          status: 403,
          title: 'Forbidden',
          detail: 'Elevated claim required (cguard:elevated). Step up first.',
          code: 'auth.elevated.required',
        },
        {
          status: 403,
          headers: { 'content-type': 'application/problem+json' },
        },
      );
    }
    const watchlist = watchlistsCatalog.find((w) => w.watchlistId === params.id);
    if (!watchlist) return new HttpResponse(null, { status: 404 });
    const body = (await request.json()) as {
      label?: string;
      category?: WatchlistEntryCategory;
      dpiaReference?: string;
      consentTimestamp?: string;
      imageBytesBase64?: string;
      referenceImageStorageKey?: string;
      enrolledByUserId?: string;
      expiresAtUtc?: string;
    };
    if (!body.label || !body.dpiaReference || !body.consentTimestamp || !body.imageBytesBase64) {
      return HttpResponse.json(
        {
          type: 'about:blank',
          status: 400,
          title: 'Validation failed',
          detail: 'label, dpiaReference, consentTimestamp, and imageBytesBase64 are required.',
          code: 'watchlists.entry.enroll_face.validation',
        },
        {
          status: 400,
          headers: { 'content-type': 'application/problem+json' },
        },
      );
    }
    const entry: WatchlistEntry = {
      entryId: crypto.randomUUID(),
      watchlistId: watchlist.watchlistId,
      type: watchlist.type,
      label: body.label,
      category: body.category ?? 'Investigation',
      dpiaReference: body.dpiaReference,
      consentTimestamp: body.consentTimestamp,
      plateIdentifier: null,
      plateRegion: null,
      hasEmbedding: true,
      enrolledAtUtc: iso(0),
      enrolledByUserId: body.enrolledByUserId ?? '00000000-0000-0000-0000-000000000000',
      expiresAtUtc: body.expiresAtUtc ?? null,
      status: 'Active',
    };
    watchlistEntries.push(entry);
    return HttpResponse.json(entry, {
      status: 201,
      headers: { location: `/v1/watchlists/entries/${entry.entryId}` },
    });
  }),
  http.post('/v1/watchlists/:id/entries/plate', async ({ request, params }) => {
    const watchlist = watchlistsCatalog.find((w) => w.watchlistId === params.id);
    if (!watchlist) return new HttpResponse(null, { status: 404 });
    const body = (await request.json()) as {
      label?: string;
      category?: WatchlistEntryCategory;
      dpiaReference?: string;
      consentTimestamp?: string;
      plate?: string;
      region?: string;
      enrolledByUserId?: string;
      expiresAtUtc?: string;
    };
    if (!body.label || !body.dpiaReference || !body.plate) {
      return HttpResponse.json(
        {
          type: 'about:blank',
          status: 400,
          title: 'Validation failed',
          detail: 'label, dpiaReference, and plate are required.',
          code: 'watchlists.entry.enroll_plate.validation',
        },
        {
          status: 400,
          headers: { 'content-type': 'application/problem+json' },
        },
      );
    }
    const entry: WatchlistEntry = {
      entryId: crypto.randomUUID(),
      watchlistId: watchlist.watchlistId,
      type: 'Plate',
      label: body.label,
      category: body.category ?? 'Banned',
      dpiaReference: body.dpiaReference,
      consentTimestamp: body.consentTimestamp ?? null,
      plateIdentifier: body.plate,
      plateRegion: body.region ?? null,
      hasEmbedding: false,
      enrolledAtUtc: iso(0),
      enrolledByUserId: body.enrolledByUserId ?? '00000000-0000-0000-0000-000000000000',
      expiresAtUtc: body.expiresAtUtc ?? null,
      status: 'Active',
    };
    watchlistEntries.push(entry);
    return HttpResponse.json(entry, {
      status: 201,
      headers: { location: `/v1/watchlists/entries/${entry.entryId}` },
    });
  }),
  http.delete('/v1/watchlists/entries/:entryId', async ({ request, params }) => {
    const entry = watchlistEntries.find((e) => e.entryId === params.entryId);
    if (!entry) return new HttpResponse(null, { status: 404 });
    const body = (await request.json()) as {
      reason?: string;
      removedByUserId?: string;
    };
    if (!body.reason) {
      return HttpResponse.json(
        {
          type: 'about:blank',
          status: 400,
          title: 'Validation failed',
          detail: 'reason is required.',
          code: 'watchlists.entry.remove.validation',
        },
        {
          status: 400,
          headers: { 'content-type': 'application/problem+json' },
        },
      );
    }
    entry.status = 'Removed';
    if (body.reason === 'RightToBeForgotten') {
      entry.hasEmbedding = false;
      entry.plateIdentifier = null;
      entry.plateRegion = null;
    }
    return new HttpResponse(null, { status: 204 });
  }),
  http.get('*/v1/admin/models', async ({ request }) => {
    const url = new URL(request.url);
    const page = Math.max(1, Number(url.searchParams.get('page') ?? 1));
    const pageSize = Math.min(200, Math.max(1, Number(url.searchParams.get('pageSize') ?? 50)));
    const includeRetired = url.searchParams.get('includeRetired') === 'true';
    const filtered = includeRetired ? models : models.filter((model) => model.status !== 'Retired');
    const start = (page - 1) * pageSize;
    return HttpResponse.json({
      items: filtered.slice(start, start + pageSize),
      page,
      pageSize,
      total: filtered.length,
      totalPages: Math.ceil(filtered.length / pageSize),
    });
  }),
  http.get('*/v1/admin/models/pins', async () => HttpResponse.json(modelPins)),
  http.get('*/v1/admin/models/:id', async ({ params }) => {
    const model = models.find((candidate) => candidate.modelId === params.id);
    return model ? HttpResponse.json(model) : new HttpResponse(null, { status: 404 });
  }),
  http.post('*/v1/admin/models', async ({ request }) => {
    const body = await readMockJsonRecord(request);
    if (
      body === null ||
      !isBoundedMockString(body.name, 128) ||
      !isBoundedMockString(body.family, 64) ||
      !MOCK_MODEL_FAMILY_PATTERN.test(body.family) ||
      !isBoundedMockString(body.architecture, 128) ||
      (body.intent !== undefined && !isOptionalBoundedMockString(body.intent, 512))
    ) {
      return new HttpResponse(null, { status: 400 });
    }
    const name = body.name.trim();
    const family = body.family;
    const architecture = body.architecture.trim();
    const intent = body.intent;
    if (models.some((candidate) => candidate.family === family)) {
      return new HttpResponse(null, { status: 409 });
    }
    const model: MockRegistryModel = {
      modelId: crypto.randomUUID(),
      name,
      family,
      architecture,
      intent:
        intent === undefined || intent === null || intent.trim() === '' ? null : intent.trim(),
      status: 'Draft',
      currentVersionId: null,
      createdAtUtc: iso(0),
      retiredAtUtc: null,
      createdByActorId: MOCK_MODEL_ACTOR_ID,
      versions: [],
    };
    models.push(model);
    return HttpResponse.json(model, {
      status: 201,
      headers: { location: '/v1/admin/models/' + model.modelId },
    });
  }),
  http.post('*/v1/admin/models/:id/versions', async ({ params, request }) => {
    const model = models.find((candidate) => candidate.modelId === params.id);
    if (!model) return new HttpResponse(null, { status: 404 });
    const body = await readMockJsonRecord(request);
    if (
      body === null ||
      !isBoundedMockString(body.semVer, 64) ||
      !MOCK_MODEL_SEMVER_PATTERN.test(body.semVer) ||
      !isBoundedMockString(body.artifactRef, 1024) ||
      (body.artifactHash !== undefined && !isOptionalBoundedMockString(body.artifactHash, 128)) ||
      model.status === 'Retired'
    ) {
      return new HttpResponse(null, { status: 400 });
    }
    const semVer = body.semVer.trim();
    const artifactRef = body.artifactRef.trim();
    const artifactHash = body.artifactHash;
    if (
      model.versions.some((candidate) => candidate.semVer.toLowerCase() === semVer.toLowerCase())
    ) {
      return new HttpResponse(null, { status: 409 });
    }
    const version: MockModelVersion = {
      versionId: crypto.randomUUID(),
      modelId: model.modelId,
      semVer,
      artifactRef,
      artifactHash:
        artifactHash === undefined || artifactHash === null || artifactHash.trim() === ''
          ? null
          : artifactHash.trim(),
      cohort: 'None',
      createdByActorId: MOCK_MODEL_ACTOR_ID,
      createdAtUtc: iso(0),
      promotedAtUtc: null,
      lastPromotedByActorId: null,
    };
    model.versions.push(version);
    return HttpResponse.json(version, {
      status: 201,
      headers: {
        location: '/v1/admin/models/' + model.modelId + '/versions/' + version.versionId,
      },
    });
  }),
  http.post('*/v1/admin/models/:id/versions/:versionId/promote', async ({ params, request }) => {
    const model = models.find((candidate) => candidate.modelId === params.id);
    const version = model?.versions.find((candidate) => candidate.versionId === params.versionId);
    const body = await readMockJsonRecord(request);
    if (
      !model ||
      !version ||
      model.status === 'Retired' ||
      body === null ||
      !isMockPromotionCohort(body.toCohort)
    ) {
      return new HttpResponse(null, { status: 400 });
    }
    if (body.toCohort === 'Prod') {
      for (const candidate of model.versions) {
        if (candidate.cohort === 'Prod') candidate.cohort = 'Staging';
      }
      model.currentVersionId = version.versionId;
      model.status = 'Active';
    } else if (model.currentVersionId === version.versionId) {
      model.currentVersionId = null;
    }
    version.cohort = body.toCohort;
    version.promotedAtUtc = iso(0);
    version.lastPromotedByActorId = MOCK_MODEL_ACTOR_ID;
    return HttpResponse.json(model);
  }),
  http.post('*/v1/admin/models/:id/versions/:versionId/demote', async ({ params }) => {
    const model = models.find((candidate) => candidate.modelId === params.id);
    const version = model?.versions.find((candidate) => candidate.versionId === params.versionId);
    if (!model || !version || model.status === 'Retired') {
      return new HttpResponse(null, { status: 400 });
    }
    version.cohort = 'None';
    version.promotedAtUtc = iso(0);
    version.lastPromotedByActorId = MOCK_MODEL_ACTOR_ID;
    if (model.currentVersionId === version.versionId) model.currentVersionId = null;
    return HttpResponse.json(model);
  }),
  http.post('*/v1/admin/models/:id/retire', async ({ params }) => {
    const model = models.find((candidate) => candidate.modelId === params.id);
    if (!model) return new HttpResponse(null, { status: 404 });
    model.status = 'Retired';
    model.retiredAtUtc = iso(0);
    model.currentVersionId = null;
    return HttpResponse.json(model);
  }),
  http.get('/v1/admin/audit', async ({ request }) => {
    const url = new URL(request.url);
    const page = Math.max(1, Number(url.searchParams.get('page') ?? 1));
    const pageSize = Math.min(200, Math.max(1, Number(url.searchParams.get('pageSize') ?? 50)));
    const actionType = url.searchParams.get('actionType') ?? '';
    const targetType = url.searchParams.get('targetType') ?? '';
    const actorUserId = url.searchParams.get('actorUserId') ?? '';
    const fromUtc = url.searchParams.get('fromUtc') ?? '';
    const toUtc = url.searchParams.get('toUtc') ?? '';
    const fromTime = fromUtc ? new Date(fromUtc).getTime() : -Infinity;
    const toTime = toUtc ? new Date(toUtc).getTime() : Infinity;
    const filtered = audit.filter((a) => {
      if (actionType && a.actionType !== actionType) return false;
      if (targetType && a.targetType !== targetType) return false;
      if (actorUserId && a.actorUserId !== actorUserId) return false;
      const t = new Date(a.occurredAtUtc).getTime();
      if (t < fromTime || t > toTime) return false;
      return true;
    });
    const total = filtered.length;
    const start = (page - 1) * pageSize;
    // PagedResult<AuditActionResponse> — { items, page, pageSize, total, totalPages }.
    return HttpResponse.json({
      items: filtered.slice(start, start + pageSize),
      page,
      pageSize,
      total,
      totalPages: Math.max(1, Math.ceil(total / pageSize)),
    });
  }),
  // AuditActorResponse[] — distinct actors with action counts (users OR service accounts).
  http.get('/v1/admin/audit/actors', async () => {
    const byActor = new Map<string, { count: number; lastSeenUtc: string; isService: boolean }>();
    for (const a of audit) {
      const key = a.actorUserId ?? a.actorServiceAccountId ?? 'system';
      const existing = byActor.get(key);
      if (existing) {
        existing.count += 1;
        if (a.occurredAtUtc > existing.lastSeenUtc) existing.lastSeenUtc = a.occurredAtUtc;
      } else {
        byActor.set(key, {
          count: 1,
          lastSeenUtc: a.occurredAtUtc,
          isService: a.actorUserId == null,
        });
      }
    }
    const actors = Array.from(byActor.entries()).map(([key, v]) => ({
      actorUserId: v.isService ? null : key,
      actorServiceAccountId: v.isService ? key : null,
      actionCount: v.count,
      lastSeenUtc: v.lastSeenUtc,
    }));
    return HttpResponse.json(actors);
  }),
  // AuditActionTypeOption[] — canonical enum names + descriptions.
  http.get('/v1/admin/audit/action-types', async () =>
    HttpResponse.json(
      AUDIT_ACTION_TYPE_CATALOG.map((c) => ({
        name: c.name,
        description: c.description,
      })),
    ),
  ),
  // CSV file (text/csv) — fromUtc + toUtc REQUIRED, optional actionType. Not a signed URL.
  http.get('/v1/admin/audit/export', async ({ request }) => {
    const url = new URL(request.url);
    const fromUtc = url.searchParams.get('fromUtc');
    const toUtc = url.searchParams.get('toUtc');
    if (!fromUtc || !toUtc) {
      return HttpResponse.json(
        { title: 'fromUtc and toUtc are required', status: 400 },
        { status: 400 },
      );
    }
    const actionType = url.searchParams.get('actionType') ?? '';
    const fromTime = new Date(fromUtc).getTime();
    const toTime = new Date(toUtc).getTime();
    const rows = audit.filter((a) => {
      if (actionType && a.actionType !== actionType) return false;
      const t = new Date(a.occurredAtUtc).getTime();
      return t >= fromTime && t <= toTime;
    });
    const csv = [
      'audit_id,occurred_at_utc,actor_user_id,actor_service_account_id,actor_ip,action_type,target_type,target_id,outcome,correlation_id',
      ...rows.map((a) =>
        [
          a.auditId,
          a.occurredAtUtc,
          a.actorUserId ?? '',
          a.actorServiceAccountId ?? '',
          a.actorIpAddress,
          a.actionType,
          a.targetType,
          a.targetId,
          a.outcomeStatus,
          a.correlationId,
        ].join(','),
      ),
    ].join('\n');
    return new HttpResponse(csv, {
      status: 200,
      headers: {
        'Content-Type': 'text/csv',
        'Content-Disposition': 'attachment; filename="audit.csv"',
      },
    });
  }),
  // Single audit event by GUID — registered LAST so the literal drill-downs win.
  http.get('/v1/admin/audit/:auditId', async ({ params }) => {
    const e = audit.find((a) => a.auditId === params.auditId);
    if (!e) return new HttpResponse(null, { status: 404 });
    return HttpResponse.json(e);
  }),
  http.get('/v1/admin/legal-holds', async ({ request }) => {
    const url = new URL(request.url);
    const status = url.searchParams.get('status')?.toLowerCase();
    const page = Math.max(1, Number(url.searchParams.get('page') ?? '1'));
    const pageSize = Math.max(1, Number(url.searchParams.get('pageSize') ?? '25'));
    const matching = legalHolds.filter((hold) => !status || hold.status === status);
    return HttpResponse.json({
      items: matching.slice((page - 1) * pageSize, page * pageSize),
      page,
      pageSize,
      total: matching.length,
      totalPages: Math.max(1, Math.ceil(matching.length / pageSize)),
    });
  }),
  http.post('/v1/admin/legal-holds', async ({ request }) => {
    const body = (await request.json()) as {
      caseRef: string;
      reason: string;
      affectsTargetType: string;
      affectsTargetIds: string[];
    };
    const targetId = body.affectsTargetIds[0];
    if (!body.caseRef || !body.reason || !targetId) {
      return new HttpResponse(null, { status: 400 });
    }
    const hold = legalHold(legalHolds.length + 1, {
      caseRef: body.caseRef,
      reason: body.reason,
      affectsTargetType: body.affectsTargetType,
      targetId,
      affectedCount: 12,
      imposedDaysAgo: 0,
    });
    legalHolds.unshift(hold);
    return HttpResponse.json(hold, { status: 201 });
  }),
  http.post('/v1/admin/legal-holds/:id/lift', async ({ params, request }) => {
    const body = (await request.json().catch(() => ({}))) as { reason?: string };
    const hold = legalHolds.find((h) => h.holdId === params.id);
    if (!hold) return new HttpResponse(null, { status: 404 });
    const liftedAtUtc = iso(0);
    hold.isActive = false;
    hold.status = 'lifted';
    hold.liftedAtUtc = liftedAtUtc;
    hold.liftContext = { liftedAtUtc, liftedByUserId: mockUserId(1), reason: body.reason ?? null };
    return new HttpResponse(null, { status: 204 });
  }),
  http.get('/v1/reports/', async () =>
    HttpResponse.json([
      {
        id: 'sira-quarterly',
        name: 'SIRA quarterly compliance',
        cadence: 'quarterly',
      },
      {
        id: 'incident-volume',
        name: 'Incident volume — all SSPs',
        cadence: 'weekly',
      },
      {
        id: 'fleet-availability',
        name: 'Fleet availability',
        cadence: 'monthly',
      },
      {
        id: 'biometric-audit',
        name: 'Biometric watchlist audit',
        cadence: 'monthly',
      },
    ]),
  ),
  http.get('/v1/report-runs/', async () => HttpResponse.json(reportRuns)),
  http.post('/v1/report-runs/', async ({ request }) => {
    const body = (await request.json()) as {
      catalogId: string;
      format?: 'pdf' | 'csv' | 'xlsx';
      from?: string;
      to?: string;
    };
    const cat = [
      { id: 'sira-quarterly', name: 'SIRA quarterly compliance' },
      { id: 'incident-volume', name: 'Incident volume — all SSPs' },
      { id: 'fleet-availability', name: 'Fleet availability' },
      { id: 'biometric-audit', name: 'Biometric watchlist audit' },
    ].find((c) => c.id === body.catalogId);
    const run: ReportRun = {
      id: `rr-${Date.now()}`,
      catalogId: body.catalogId,
      name: cat?.name ?? body.catalogId,
      ranAt: iso(0),
      ranBy: 'platform-admin@uxe.ai',
      status: 'queued',
      format: body.format ?? 'pdf',
    };
    reportRuns.unshift(run);
    return HttpResponse.json(run, { status: 202 });
  }),
  // SiraReportPreviewResponse as Central builds it today: per-tier counts only, an empty
  // status map and SSP breakdown, and the same blocking issues.
  http.get('/v1/admin/reports/sira-preview', async ({ request }) => {
    const windowDays = Number(new URL(request.url).searchParams.get('windowDays') ?? '7');
    const byTier = { '1': 9, '2': 64, '3': 118, '4': 37, '5': 6 };
    const body: SiraReportPreviewResponse = {
      reportPeriodStartUtc: iso(-windowDays * DAY_MS),
      reportPeriodEndUtc: iso(0),
      incidentSummary: {
        total: Object.values(byTier).reduce((sum, count) => sum + count, 0),
        byTier,
        byStatus: {},
      },
      sspBreakdown: [],
      readyToSubmit: false,
      blockingIssues: [
        'sira_submit endpoint not implemented yet',
        'incidents-by-status breakdown not yet exposed by IIncidentsService (returns empty map)',
        'per-SSP incident rollup + SLA attainment not yet exposed by IIncidentsService (returns empty list)',
      ],
    };
    return HttpResponse.json(body);
  }),
  http.get('/v1/admin/configuration', async () => HttpResponse.json(configurationEntries)),
  http.put('/v1/admin/configuration/:configKey', async ({ params, request }) => {
    const url = new URL(request.url);
    const scope = url.searchParams.get('scope') ?? 'Global';
    const scopeId = url.searchParams.get('scopeId');
    const body = (await request.json()) as { valueJson: string };
    const entry = configurationEntries.find(
      (c) => c.configKey === params.configKey && c.scope === scope && c.scopeId === scopeId,
    );
    if (!entry) return new HttpResponse(null, { status: 404 });
    entry.valueJson = body.valueJson;
    entry.lastUpdatedAtUtc = iso(0);
    return HttpResponse.json(entry);
  }),
  http.get('/v1/admin/permissions/', async ({ request }) => {
    const url = new URL(request.url);
    const pageParameter = parsePermissionIntegerQueryParameter(url.searchParams, 'page');
    const pageSizeParameter = parsePermissionIntegerQueryParameter(url.searchParams, 'pageSize');
    if (pageParameter.kind === 'invalid' || pageSizeParameter.kind === 'invalid') {
      return new HttpResponse(null, { status: 400 });
    }
    const page = Math.max(1, pageParameter.kind === 'valid' ? pageParameter.value : 1);
    const pageSize = Math.min(
      200,
      Math.max(1, pageSizeParameter.kind === 'valid' ? pageSizeParameter.value : 50),
    );
    const roleNameFilter = url.searchParams.get('roleNameFilter');
    const filtered =
      roleNameFilter !== null && roleNameFilter.trim() !== ''
        ? rolePermissions.filter((permission) => permission.roleName === roleNameFilter)
        : rolePermissions;
    const total = filtered.length;
    const start = (page - 1) * pageSize;
    return HttpResponse.json({
      items: filtered.slice(start, start + pageSize),
      page,
      pageSize,
      total,
      totalPages: Math.ceil(total / pageSize),
    });
  }),
  http.get('/v1/identity/users/:id/sessions', async ({ params }) =>
    HttpResponse.json(platformSessions.filter((s) => s.userId === params.id)),
  ),
  http.post('/v1/identity/sessions/:sessionId/revoke', async ({ params }) => {
    const idx = platformSessions.findIndex((s) => s.sessionId === params.sessionId);
    if (idx === -1) return new HttpResponse(null, { status: 404 });
    platformSessions.splice(idx, 1);
    return new HttpResponse(null, { status: 204 });
  }),
  http.get('/v1/tenants/ssps/:id/detail', async ({ params }) => {
    const ssp = ssps.find((item) => centralSspId(item.id) === params.id);
    if (!ssp) return new HttpResponse(null, { status: 404 });
    const sspSsus = ssus.filter((item) => item.sspId === ssp.id);
    return HttpResponse.json({
      ssp: toCentralSsp(ssp),
      ssus: sspSsus.map(toCentralSsu),
      audit: audit.slice(0, 20),
      contract: {
        startUtc: ssp.contractStartUtc ?? ssp.createdAt,
        billingPlan: ssp.billingPlan ?? (ssp.id === 'ssp-acme' ? 'Enterprise' : 'Standard'),
      },
    });
  }),

  // ---- Step-up auth (WebAuthn re-prompt) — mock challenge/verify ----
  http.post('/api/auth/step-up/challenge', async () =>
    HttpResponse.json({
      challengeId: `chl-${Date.now()}`,
      challenge: 'mock-base64url-challenge',
      expiresInSec: 90,
    }),
  ),
  http.post('/api/auth/step-up/verify', async ({ request }) => {
    const body = (await request.json()) as { challengeId: string };
    if (!body.challengeId) return new HttpResponse(null, { status: 400 });
    return HttpResponse.json({
      stepUpToken: `stepup-${Date.now()}`,
      ttlSec: 300,
    });
  }),

  // ---- SSP Portal extensions (sub-agent 2026-04-30) ----------------------
  // These shapes are forward-compatible drafts pending OpenAPI fragments
  // from the central .NET monolith. Field names mirror the planned spec so
  // codegen swap-out stays mechanical.

  // Portfolio incidents-by-tier roll-up for the home dashboard.
  http.get('/api/ssp/incidents/by-tier', async ({ request }) => {
    const url = new URL(request.url);
    const windowH = Number(url.searchParams.get('windowHours') ?? 24);
    // Synthesize a deterministic shape from the existing fixtures so the
    // chart never reads as "empty" in dev. Production will return real
    // counts grouped by tier from AlarmsIncidents.
    const base = { 1: 2, 2: 6, 3: 11, 4: 18, 5: 10 } as Record<1 | 2 | 3 | 4 | 5, number>;
    const total = Object.values(base).reduce((a, b) => a + b, 0);
    return HttpResponse.json({ windowHours: windowH, total, byTier: base });
  }),

  // SSP camera list/detail were previously served by /api/ssp/cameras with
  // richer SSP-rollup shape. They now reuse the canonical /v1/cameras and
  // /v1/cameras/{id} handlers above; the SSP-specific tier/cohort columns
  // remain a backend gap (audit-tracked separately).

  // Billing reconciliation — line-item drilldown plus discrepancy report.
  http.get('/api/ssp/billing/reconciliation', async () => {
    return HttpResponse.json({
      generatedAt: iso(0),
      perSsu: ssus.map((s, i) => ({
        ssuId: s.id,
        ssuName: s.name,
        sitesBilled: s.siteCount,
        camerasBilled: s.siteCount * 8 + i,
        camerasObserved: s.siteCount * 8 + i + (i === 1 ? 2 : 0),
        edgeAppliancesBilled: s.siteCount,
        edgeAppliancesObserved: s.siteCount,
        chargesAed: s.siteCount * 200 + i * 80,
      })),
      discrepancies:
        ssus.length > 1
          ? [
              {
                id: 'disc-001',
                ssuId: ssus[1]!.id,
                ssuName: ssus[1]!.name,
                kind: 'camera-count-mismatch' as const,
                billed: ssus[1]!.siteCount * 8 + 1,
                observed: ssus[1]!.siteCount * 8 + 3,
                deltaUnits: 2,
                impactAed: 200,
                detectedAt: iso(-3 * 60 * 60 * 1000),
              },
            ]
          : [],
    });
  }),
  http.get('/api/ssp/billing/export.csv', async () => {
    const csv = [
      'ssu_id,ssu_name,sites_billed,cameras_billed,cameras_observed,charges_aed',
      ...ssus.map(
        (s, i) =>
          `${s.id},${s.name},${s.siteCount},${s.siteCount * 8 + i},${s.siteCount * 8 + i + (i === 1 ? 2 : 0)},${s.siteCount * 200 + i * 80}`,
      ),
    ].join('\n');
    return new HttpResponse(csv, {
      status: 200,
      headers: {
        'content-type': 'text/csv',
        'content-disposition': `attachment; filename="ssp-billing-${Date.now()}.csv"`,
      },
    });
  }),

  // Reports — recurring schedules + SIRA submission preview.
  http.get('/api/ssp/reports/schedules', async () =>
    HttpResponse.json([
      {
        id: 'sched-001',
        catalogId: 'sla-monthly',
        catalogName: 'SLA attainment · monthly',
        cadence: 'monthly' as const,
        format: 'pdf' as const,
        nextRunAt: iso(7 * 24 * 60 * 60 * 1000),
        recipients: ['ops@acme-security.ae'],
        active: true,
      },
      {
        id: 'sched-002',
        catalogId: 'edge-fleet-health',
        catalogName: 'Edge fleet health · weekly',
        cadence: 'weekly' as const,
        format: 'pdf' as const,
        nextRunAt: iso(2 * 24 * 60 * 60 * 1000),
        recipients: ['noc@acme-security.ae'],
        active: true,
      },
    ]),
  ),
  http.post('/api/ssp/reports/schedules', async ({ request }) => {
    const body = (await request.json()) as Record<string, unknown>;
    return HttpResponse.json(
      {
        id: `sched-${Date.now()}`,
        ...body,
        active: true,
        nextRunAt: iso(7 * 24 * 60 * 60 * 1000),
      },
      { status: 201 },
    );
  }),
  http.get('/api/ssp/reports/sira-preview', async () => {
    return HttpResponse.json({
      previewBuiltAt: iso(0),
      coveragePeriod: {
        start: iso(-30 * 24 * 60 * 60 * 1000),
        end: iso(0),
      },
      summary: {
        sitesCovered: 87,
        incidentsTotal: 412,
        tier1: 8,
        tier2: 47,
        slaAttainment: 0.987,
      },
      submitWindow: {
        opens: iso(2 * 24 * 60 * 60 * 1000),
        closes: iso(7 * 24 * 60 * 60 * 1000),
      },
      // TODO(backend): real Reporting module endpoint will return a signed PDF URL.
      previewUrl: '/exports/sira-preview-mock.pdf?sig=mock',
    });
  }),

  // Settings — API tokens (read-only listing + revoke), notification prefs.
  http.get('/api/ssp/settings/api-tokens', async () =>
    HttpResponse.json([
      {
        id: 'tok-001',
        name: 'NOC desk laptop',
        last4: 'k7Xq',
        scope: 'ssp.read',
        createdAt: iso(-90 * 24 * 60 * 60 * 1000),
        lastUsedAt: iso(-2 * 60 * 60 * 1000),
      },
      {
        id: 'tok-002',
        name: 'Billing export script',
        last4: 'p2Mn',
        scope: 'ssp.billing.read',
        createdAt: iso(-30 * 24 * 60 * 60 * 1000),
        lastUsedAt: iso(-1 * 24 * 60 * 60 * 1000),
      },
    ]),
  ),
  http.post('/api/ssp/settings/api-tokens/:id/revoke', async ({ params }) =>
    HttpResponse.json({ id: params.id, revokedAt: iso(0) }),
  ),
  http.get('/api/ssp/settings/notifications', async () =>
    HttpResponse.json({
      emailDigest: true,
      alarmThresholdPerMinute: 8,
      digestRecipients: ['ops@acme-security.ae'],
    }),
  ),
  http.put('/api/ssp/settings/notifications', async ({ request }) =>
    HttpResponse.json(await request.json()),
  ),
  http.get('/api/ssp/settings/profile', async () =>
    HttpResponse.json({
      displayName: 'Samira K.',
      locale: 'en',
      contactEmail: 'samira.k@acme-security.ae',
    }),
  ),
  http.put('/api/ssp/settings/profile', async ({ request }) =>
    HttpResponse.json(await request.json()),
  ),
];

export const handlers = createHandlers();
